import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { MapContainer, TileLayer, CircleMarker, Popup, Rectangle, Tooltip, useMap, useMapEvents } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import 'leaflet.markercluster/dist/leaflet.markercluster.js'
import 'leaflet.markercluster/dist/MarkerCluster.css'
import 'leaflet.markercluster/dist/MarkerCluster.Default.css'
import PointDataTableTrawl from './PointDataTableTrawl'
import PointDataTableLongline from './PointDataTableLongline'
import PointDataTableLonglineBiology from './PointDataTableLonglineBiology'
import PointDataTableTrawlBiology from './PointDataTableTrawlBiology'
import PointDataTablePlankton from './PointDataTablePlankton'
import PointDataTablePlanktonBiology from './PointDataTablePlanktonBiology'
import ResultsExportBar from './ResultsExportBar'
import MaterialTabs from './materialTabs'
import { PLANKTON_WEBMAP_ENABLED } from '../features'
import FilterOverlay from './FilterOverlay'
import DateRangeFields from './DateRangeFields'
import ShapefileLayer from './ShapefileLayer'
import { downloadBlob } from '../utils/downloadCsv'
import { getZipFilename } from './ResultsExportBar'

import { Snackbar, Typography, Paper, Box, FormControl, Select, MenuItem, Button, CircularProgress, Autocomplete, TextField, FormControlLabel, Switch, Chip, useMediaQuery, Dialog, DialogTitle, DialogContent, DialogActions, Drawer } from '@mui/material'
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'
import bllTaxons from '../unique_longline_taxons_current.csv'
import trawlTaxons from '../unique_trawl_taxons.csv'
import seamapLogo from '../images/seamap_logo.png'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import LayersIcon from '@mui/icons-material/Layers'
import CheckIcon from '@mui/icons-material/Check'
import { commonSwitchStyles } from './SwitchStyles'

// Enhanced color palette with better contrast - using distinctive colors that
// work well on maps. ESRI World Imagery remains available as a basemap option,
// so any earth/tan/beige/khaki tone can disappear into near-shore sand,
// bare ground, and urban pixels. Olive (#808000), Silver (#C0C0C0), and Light
// Yellow (#FFFF80) were removed for that reason. The `isLandTone` predicate
// below is the long-term safety net: even if someone adds a new color here
// later, the contrast picker will refuse to hand it out if it falls in the
// land-tone wedge.
const SPECIES_COLORS = [
    '#FF0000', // Bright Red
    '#00FF00', // Bright Green
    '#0000FF', // Bright Blue
    '#FFFF00', // Bright Yellow
    '#FF00FF', // Magenta
    '#00FFFF', // Cyan
    '#FF8000', // Orange
    '#8000FF', // Purple
    '#80FF00', // Lime
    '#FF0080', // Hot Pink
    '#0080FF', // Azure
    '#FF8080', // Light Red
    '#80FF80', // Light Green
    '#8080FF', // Light Blue
    '#FF80FF', // Light Magenta
    '#80FFFF', // Light Cyan
    '#800000', // Dark Red
    '#008000', // Dark Green
    '#000080', // Dark Blue
    '#800080', // Dark Magenta
    '#008080', // Teal
    '#FFA500', // Orange Alt
    '#32CD32', // Lime Green
    '#8A2BE2', // Blue Violet
    '#DC143C', // Crimson
    '#00CED1', // Dark Turquoise
    '#FF1493'  // Deep Pink
]

const BASEMAP_OPTIONS = [
    {
        id: 'ocean',
        label: 'Ocean Bathy',
        type: 'tile',
        url: 'https://server.arcgisonline.com/arcgis/rest/services/Ocean/World_Ocean_Base/MapServer/tile/{z}/{y}/{x}',
        maxZoom: 16,
        maxNativeZoom: 16
    },
    {
        id: 'imagery',
        label: 'Imagery',
        type: 'tile',
        url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        maxZoom: 19
    },
    {
        id: 'minimal',
        label: 'Minimal',
        type: 'vector'
    }
]

const MINIMAL_BASEMAP = {
    land: '#29474d',
    water: '#a8c9d3',
    coastline: '#4f6f58',
    shorelineUrl: `${process.env.PUBLIC_URL}/spatial/iho.shp`,
    shorelineBounds: {
        north: 31 + (35 / 60),
        west: -(98 + (42 / 60)),
        east: -(78 + (5 / 60)),
        south: 17 + (39 / 60)
    }
}

function MinimalBasemapLayer() {
    return (
        <ShapefileLayer
            url={MINIMAL_BASEMAP.shorelineUrl}
            borderColor={MINIMAL_BASEMAP.coastline}
            fillColor={MINIMAL_BASEMAP.water}
            fillOpacity={1}
            opacity={0.92}
            weight={1.25}
        />
    )
}

const trackUsageEvent = (payload) => {
    try {
        const body = JSON.stringify(payload)
        const usageEventUrl = `${API_URL}/usage/event`
        if (navigator.sendBeacon) {
            const queued = navigator.sendBeacon(usageEventUrl, new Blob([body], { type: 'application/json' }))
            if (queued) return
        }
        fetch(usageEventUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
            keepalive: true
        }).catch(() => {})
    } catch (_) {
        // Usage tracking is best-effort.
    }
}

// Color contrast calculation utilities
const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : null
}

// Calculate relative luminance for contrast calculation
const getLuminance = (rgb) => {
    const { r, g, b } = rgb
    const [rs, gs, bs] = [r, g, b].map(c => {
        c = c / 255
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
    })
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs
}

// Calculate contrast ratio between two colors (WCAG standard)
const getContrastRatio = (color1, color2) => {
    const rgb1 = hexToRgb(color1)
    const rgb2 = hexToRgb(color2)

    if (!rgb1 || !rgb2) return 0

    const lum1 = getLuminance(rgb1)
    const lum2 = getLuminance(rgb2)

    const brightest = Math.max(lum1, lum2)
    const darkest = Math.min(lum1, lum2)

    return (brightest + 0.05) / (darkest + 0.05)
}

// Convert hex â†’ HSL so we can reason about hue/saturation/lightness ranges
// (used by `isLandTone` to detect colors that fade into satellite-imagery
// land pixels). Returns null for invalid hex strings.
const hexToHsl = (hex) => {
    const rgb = hexToRgb(hex)
    if (!rgb) return null
    const r = rgb.r / 255, g = rgb.g / 255, b = rgb.b / 255
    const max = Math.max(r, g, b), min = Math.min(r, g, b)
    const l = (max + min) / 2
    let h = 0, s = 0
    if (max !== min) {
        const d = max - min
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break
            case g: h = (b - r) / d + 2; break
            case b: h = (r - g) / d + 4; break
            default: break
        }
        h *= 60
    }
    return { h, s: s * 100, l: l * 100 }
}

// Reject colors that visually disappear into the ESRI World Imagery basemap
// at the coast. Two failure modes are blocked:
//   1. Warm earth-tone wedge (tans, beiges, khakis, sand): hues 15Â°â€“75Â° with
//      saturation under 65% and a mid lightness band. Vivid warm hues like
//      #FF8000 / #FFA500 (saturation 100%) are intentionally still allowed.
//   2. Near-neutral grays at any meaningful lightness: they fade into urban
//      pixels, cloud shadows, and pale sand alike.
// This is a safety net on top of curating the palette by hand â€” if anyone
// adds a new color above, the contrast picker will simply skip it should it
// land in the no-go zone.
const isLandTone = (hex) => {
    const hsl = hexToHsl(hex)
    if (!hsl) return false
    const { h, s, l } = hsl
    if (h >= 15 && h <= 75 && s < 65 && l >= 25 && l <= 90) return true
    if (s < 12 && l >= 25 && l <= 90) return true
    return false
}

// Find the best color with sufficient contrast from already assigned colors.
// Land-tone candidates are skipped by `isLandTone` so a bad color can never
// reach a point even if the palette is edited later.
const findContrastingColor = (assignedColors, minContrastRatio = 2.5) => {
    const safePalette = SPECIES_COLORS.filter(c => !isLandTone(c))

    // If no colors assigned yet, return the first safe color
    if (assignedColors.length === 0) {
        return safePalette[0]
    }

    // Try each color in our (already land-tone-filtered) palette
    for (const candidateColor of safePalette) {
        // Skip if this color is already assigned
        if (assignedColors.includes(candidateColor)) {
            continue
        }

        // Check contrast against all assigned colors
        const hasGoodContrast = assignedColors.every(assignedColor => {
            const contrast = getContrastRatio(candidateColor, assignedColor)
            return contrast >= minContrastRatio
        })

        if (hasGoodContrast) {
            return candidateColor
        }
    }

    // If no safe color meets the contrast requirement, return the next unused
    // safe color, then finally cycle within the safe palette so we never fall
    // back onto a tan/beige/silver hue.
    const unusedColors = safePalette.filter(color => !assignedColors.includes(color))
    return unusedColors.length > 0 ? unusedColors[0] : safePalette[assignedColors.length % safePalette.length]
}

// Global color assignment tracking for contrast validation
let globalSpeciesColorMap = new Map()

// Function to get a consistent color for a species with contrast validation
const getSpeciesColor = (item, selectedSpecies, allData = []) => {
    // Priority 1: Use synonym_group if available (this groups synonyms together)
    let speciesKey = item.synonym_group

    // Priority 2: Use matched species fields from backend
    if (!speciesKey) {
        speciesKey = item.ciu_taxon || item.CIU_TAXON || item.matched_taxon || item.matched_species
    }

    // Priority 3: Use the genus-species format from raw data
    if (!speciesKey) {
        if (item.genus_bgs && item.spec_bgs) {
            // For trawl biology data
            speciesKey = `${item.genus_bgs} ${item.spec_bgs}`
        } else if (item.GENUS && item.SPECIES) {
            // For longline biology data
            speciesKey = `${item.GENUS} ${item.SPECIES}`
        }
    }

    // Priority 4: Fall back to first selected species if we can't determine the species
    if (!speciesKey && Array.isArray(selectedSpecies) && selectedSpecies.length > 0) {
        speciesKey = selectedSpecies[0]
    }

    if (!speciesKey) {
        return '#b8d8d0' // Default color if we can't determine the species
    }

    // If we already have a color for this species, return it
    if (globalSpeciesColorMap.has(speciesKey)) {
        return globalSpeciesColorMap.get(speciesKey)
    }

    // Get currently assigned colors for contrast checking
    const assignedColors = Array.from(globalSpeciesColorMap.values())

    // Find a color with good contrast
    const newColor = findContrastingColor(assignedColors, 2.5)

    // Store the color assignment
    globalSpeciesColorMap.set(speciesKey, newColor)

    return newColor
}

// Function to reset color assignments (useful when loading new data)
const resetSpeciesColors = () => {
    globalSpeciesColorMap.clear()
}

// Function to get unique species from data with their colors
const getSpeciesLegend = (data, selectedSpecies) => {
    const speciesMap = new Map()

    // Pre-process all species to ensure consistent color assignment
    data.forEach(item => {
        // Get species color first to ensure it's assigned in the global map
        getSpeciesColor(item, selectedSpecies, data)
    })

    data.forEach(item => {
        // Get the species identifier using the same priority logic as getSpeciesColor
        let speciesKey = item.synonym_group
        let displayName = speciesKey

        // Use matched species if no synonym group
        if (!speciesKey) {
            speciesKey = item.ciu_taxon || item.CIU_TAXON || item.matched_taxon || item.matched_species
            displayName = speciesKey
        }

        // Use genus-species format if no backend info
        if (!speciesKey) {
            if (item.genus_bgs && item.spec_bgs) {
                // For trawl biology data
                speciesKey = `${item.genus_bgs} ${item.spec_bgs}`
                displayName = speciesKey
            } else if (item.GENUS && item.SPECIES) {
                // For longline biology data
                speciesKey = `${item.GENUS} ${item.SPECIES}`
                displayName = speciesKey
            }
        }

        if (speciesKey && !speciesMap.has(speciesKey)) {
            // For display, prefer the matched species name over synonym group codes
            let finalDisplayName = displayName
            if (item.ciu_taxon || item.CIU_TAXON) {
                finalDisplayName = item.ciu_taxon || item.CIU_TAXON
            } else if (item.matched_taxon) {
                finalDisplayName = item.matched_taxon
            } else if (item.matched_species) {
                finalDisplayName = item.matched_species
            }

            speciesMap.set(speciesKey, {
                name: finalDisplayName,
                color: getSpeciesColor(item, selectedSpecies, data)
            })
        }
    })

    return Array.from(speciesMap.values()).sort((a, b) => a.name.localeCompare(b.name))
}

// API URL constant - use relative URLs for IIS deployment
const API_URL = process.env.REACT_APP_API_URL || 'api'
const HEATMAP_GRID_SYSTEM_ID = 'seamap-10min-v1'
const HEATMAP_MAX_RAMP_CLASSES = 6
const HEATMAP_TOOLTIP_DELAY_MS = 350
const COMPACT_BIOLOGY_OVERLAY_QUERY = '@media (max-height: 760px), (max-width: 1180px)'
const FILTER_OVERLAY_Z_INDEX = 1700
const HEATMAP_ZERO_CELL_BORDER = '#2E5A88'
const HEATMAP_IMAGERY_ZERO_CELL_BORDER = '#FFE680'

const getReturnLinkConfig = () => {
    if (typeof window === 'undefined') {
        return {
            label: 'Back to Data Access',
            href: 'https://seamapdata.gsmfc.org/pages/viewer.php'
        }
    }

    const host = window.location.hostname.toLowerCase()
    const path = window.location.pathname.toLowerCase()
    const isGsmfcProxy = host === 'www.gsmfc.org' && path.startsWith('/seamapgis')
    const dataAccessHost = host === 'seamap.gsmfc.org'
        ? 'https://seamap.gsmfc.org'
        : 'https://seamapdata.gsmfc.org'

    return isGsmfcProxy
        ? {
            label: 'Back to GSMFC.org',
            href: 'https://www.gsmfc.org/'
        }
        : {
            label: 'Back to Data Access',
            href: `${dataAccessHost}/pages/viewer.php`
        }
}

const renderMutedSelectPlaceholder = (label) => (
    <Box component="span" sx={{ color: 'rgba(255, 245, 230, 0.52)', fontStyle: 'normal', fontWeight: 400 }}>
        {label}
    </Box>
)

const HEATMAP_QUANTILE_COLORS = [
    '#fff7ec',
    '#fdd49e',
    '#fdbb84',
    '#fc8d59',
    '#d7301f',
    '#7f0000'
]
const HEATMAP_ENCOUNTER_COLORS = ['#fff7ec', '#fdd49e', '#fdbb84', '#fc8d59', '#d7301f', '#7f0000']

const getHeatmapMetricKey = (metadata = {}) => {
    const metric = metadata.metric || 'raw'
    const isHookEffort = metadata.effort_method === 'hooks_deployed'
    if (metric === 'raw') return 'catch_count'
    if (metric === 'effort') return isHookEffort ? 'hooks_deployed' : 'effort_hectares'
    if (metric === 'encounter_rate') return 'encounter_rate'
    if (metric === 'biomass_cpue') return isHookEffort ? 'cpue_kg_100_hooks' : 'cpue_kg_hectare'
    return isHookEffort ? 'cpue_count_100_hooks' : 'cpue_count_hectare'
}

const formatLegendValue = (value, unit = '') => {
    const number = Number(value)
    if (!Number.isFinite(number)) return 'n/a'
    if (unit.includes('%')) {
        return `${number.toLocaleString(undefined, { maximumFractionDigits: number < 10 ? 1 : 0 })}%`
    }
    if (unit === 'individuals' || unit === 'hooks deployed') {
        return number.toLocaleString(undefined, { maximumFractionDigits: 0 })
    }
    const maximumFractionDigits = number >= 100 ? 0 : number >= 10 ? 1 : number >= 1 ? 2 : 3
    return number.toLocaleString(undefined, { maximumFractionDigits })
}

const pickSequentialColor = (index, total, colors) => {
    if (total <= 1) return colors[colors.length - 1]
    const colorIndex = Math.round((index / (total - 1)) * (colors.length - 1))
    return colors[colorIndex]
}

const buildQuantileScale = (values, colors, unit = '') => {
    const rampColors = colors.slice(0, HEATMAP_MAX_RAMP_CLASSES)
    const sortedValues = values
        .map(value => Number(value))
        .filter(value => Number.isFinite(value) && value > 0)
        .sort((a, b) => a - b)

    if (sortedValues.length === 0) {
        return {
            scaleType: 'value classes',
            classes: [],
            getColor: () => null
        }
    }

    const upperBounds = []
    for (let index = 0; index < rampColors.length; index += 1) {
        const upperIndex = Math.min(
            sortedValues.length - 1,
            Math.max(0, Math.ceil(((index + 1) / rampColors.length) * sortedValues.length) - 1)
        )
        const upper = sortedValues[upperIndex]
        if (upperBounds.length === 0 || upper > upperBounds[upperBounds.length - 1]) {
            upperBounds.push(upper)
        }
    }

    const classes = upperBounds.map((upper, index) => {
        const lower = index === 0 ? 0 : upperBounds[index - 1]
        const lowerLabel = index === 0 ? '>0' : `>${formatLegendValue(lower, unit)}`
        return {
            minExclusive: lower,
            max: upper,
            color: pickSequentialColor(index, upperBounds.length, rampColors),
            label: `${lowerLabel}-${formatLegendValue(upper, unit)}`
        }
    })

    return {
        scaleType: 'value classes',
        classes,
        getColor: (value) => {
            const number = Number(value)
            if (!Number.isFinite(number) || number <= 0) return null
            const match = classes.find(item => number <= item.max)
            return match ? match.color : classes[classes.length - 1]?.color || rampColors[rampColors.length - 1]
        }
    }
}

const buildEncounterRateScale = () => {
    const classes = [
        { minExclusive: 0, max: 10, color: HEATMAP_ENCOUNTER_COLORS[0], label: '>0-10%' },
        { minExclusive: 10, max: 25, color: HEATMAP_ENCOUNTER_COLORS[1], label: '10-25%' },
        { minExclusive: 25, max: 50, color: HEATMAP_ENCOUNTER_COLORS[2], label: '25-50%' },
        { minExclusive: 50, max: 75, color: HEATMAP_ENCOUNTER_COLORS[3], label: '50-75%' },
        { minExclusive: 75, max: 90, color: HEATMAP_ENCOUNTER_COLORS[4], label: '75-90%' },
        { minExclusive: 90, max: 100, color: HEATMAP_ENCOUNTER_COLORS[5], label: '90-100%' }
    ]
    return {
        scaleType: 'percent classes',
        classes,
        getColor: (value) => {
            const number = Number(value)
            if (!Number.isFinite(number) || number <= 0) return null
            const clamped = Math.min(100, number)
            const match = classes.find(item => clamped <= item.max)
            return match ? match.color : classes[classes.length - 1].color
        }
    }
}

const buildHeatmapColorScale = (cells = [], metadata = {}) => {
    const metricKey = getHeatmapMetricKey(metadata)
    const unit = metadata.unit || ''
    if (metadata.metric === 'encounter_rate') {
        return {
            metricKey,
            ...buildEncounterRateScale()
        }
    }
    const values = Array.isArray(cells)
        ? cells.map(cell => Number(cell?.[metricKey] || 0)).filter(value => value > 0)
        : []
    return {
        metricKey,
        ...buildQuantileScale(values, HEATMAP_QUANTILE_COLORS, unit)
    }
}

const getHeatmapMetricInfo = (metadata = {}) => {
    const isHookEffort = metadata?.effort_method === 'hooks_deployed'
    const bottomLonglineDescriptions = {
        raw: "# individuals / 10' x 10' cell",
        cpue: "# individuals / 100 hooks / 10' x 10' cell",
        biomass_cpue: "kg / 100 hooks / 10' x 10' cell",
        effort: "# hooks / 10' x 10' cell",
        encounter_rate: '% stations with positive captures'
    }
    const trawlDescriptions = {
        raw: "# individuals / 10' x 10' cell",
        cpue: "# individuals / hectare swept / 10' x 10' cell",
        biomass_cpue: "kg / hectare swept / 10' x 10' cell",
        effort: "hectare swept / 10' x 10' cell",
        encounter_rate: '% stations with positive captures'
    }
    switch (metadata?.metric) {
        case 'raw':
            return {
                label: 'Total Catch',
                description: isHookEffort ? bottomLonglineDescriptions.raw : trawlDescriptions.raw
            }
        case 'cpue':
            return {
                label: 'Relative Abundance',
                description: isHookEffort ? bottomLonglineDescriptions.cpue : trawlDescriptions.cpue
            }
        case 'biomass_cpue':
            return {
                label: 'Relative Biomass',
                description: isHookEffort ? bottomLonglineDescriptions.biomass_cpue : trawlDescriptions.biomass_cpue
            }
        case 'effort':
            return {
                label: 'Effort',
                description: isHookEffort ? bottomLonglineDescriptions.effort : trawlDescriptions.effort
            }
        case 'encounter_rate':
            return {
                label: 'Encounter Rate',
                description: isHookEffort ? bottomLonglineDescriptions.encounter_rate : trawlDescriptions.encounter_rate
            }
        default:
            return {
                label: 'Heat map',
                description: metadata?.unit || 'Cell values'
            }
    }
}

const isFiniteCellNumber = (value) => (
    value !== null &&
    value !== undefined &&
    value !== '' &&
    Number.isFinite(Number(value))
)

const formatHeatmapCount = (value) => {
    if (!isFiniteCellNumber(value)) return 'n/a'
    const number = Number(value)
    return number.toLocaleString(undefined, { maximumFractionDigits: 0 })
}

const formatHeatmapDecimal = (value, digits = 2) => {
    if (!isFiniteCellNumber(value)) return 'n/a'
    const number = Number(value)
    return number.toLocaleString(undefined, { maximumFractionDigits: digits })
}

const formatHeatmapPercent = (value) => {
    if (!isFiniteCellNumber(value)) return 'n/a'
    const number = Number(value)
    return `${number.toLocaleString(undefined, { maximumFractionDigits: number < 10 ? 1 : 0 })}%`
}

const formatHeatmapBound = (value) => {
    if (!isFiniteCellNumber(value)) return 'n/a'
    const number = Number(value)
    return number.toFixed(3)
}

const formatHeatmapUnitLabel = (unit = '') => {
    if (!unit) return 'n/a'
    return String(unit).replace(/\//g, ' / ')
}

const buildHeatmapCellTooltipRows = (cell = {}, metadata = {}, metricKey = 'catch_count', metricValue = null) => {
    const isHookEffort = metadata?.effort_method === 'hooks_deployed'
    const effortValue = isHookEffort
        ? cell.hooks_deployed
        : (cell.effort_hectares ?? cell.swept_area_hectares)
    const rows = [
        ['Value', isFiniteCellNumber(metricValue) ? formatLegendValue(metricValue, metadata?.unit || '') : 'n/a'],
        ['Unit', formatHeatmapUnitLabel(metadata?.unit)],
        ['Total catch', formatHeatmapCount(cell.catch_count)]
    ]

    if (isFiniteCellNumber(cell.catch_weight_kg)) {
        rows.push(['Catch weight', `${formatHeatmapDecimal(cell.catch_weight_kg, 2)} kg`])
    }

    if (isHookEffort) {
        rows.push(
            ['Sets', formatHeatmapCount(cell.set_count)],
            ['Positive sets', formatHeatmapCount(cell.positive_set_count)],
            ['Zero-catch sets', formatHeatmapCount(cell.zero_catch_set_count)],
            ['Hooks deployed', formatHeatmapCount(effortValue)]
        )
    } else {
        rows.push(
            ['Tows', formatHeatmapCount(cell.tow_count)],
            ['Positive tows', formatHeatmapCount(cell.positive_tow_count)],
            ['Zero-catch tows', formatHeatmapCount(cell.zero_catch_tow_count)],
            ['Hectares swept', formatHeatmapDecimal(effortValue, 2)]
        )
    }

    if (metricKey !== 'encounter_rate' && isFiniteCellNumber(cell.encounter_rate)) {
        rows.push(['Encounter rate', formatHeatmapPercent(cell.encounter_rate)])
    }

    rows.push(
        ['Latitude', `${formatHeatmapBound(cell.south)} to ${formatHeatmapBound(cell.north)}`],
        ['Longitude', `${formatHeatmapBound(cell.west)} to ${formatHeatmapBound(cell.east)}`]
    )

    return rows
}

function HeatmapCellTooltipContent({ cell, metadata, metricKey, metricValue }) {
    const metricInfo = getHeatmapMetricInfo(metadata)
    const rows = buildHeatmapCellTooltipRows(cell, metadata, metricKey, metricValue)

    return (
        <div className="heatmap-cell-tooltip-content">
            <div className="heatmap-cell-tooltip-title">{metricInfo.label}</div>
            <div className="heatmap-cell-tooltip-grid">
                {rows.map(([label, value]) => (
                    <React.Fragment key={label}>
                        <span className="heatmap-cell-tooltip-label">{label}</span>
                        <span className="heatmap-cell-tooltip-value">{value}</span>
                    </React.Fragment>
                ))}
            </div>
        </div>
    )
}

function HeatmapCellRectangle({ cell, bounds, pathOptions, interactive, metadata, metricKey, metricValue, cellKey }) {
    const map = useMap()
    const rectangleRef = useRef(null)
    const hoverTimerRef = useRef(null)
    const lastLatLngRef = useRef(null)
    const [showTooltip, setShowTooltip] = useState(false)
    const [tooltipDirection, setTooltipDirection] = useState('top')

    const clearHoverTimer = useCallback(() => {
        if (hoverTimerRef.current) {
            clearTimeout(hoverTimerRef.current)
            hoverTimerRef.current = null
        }
    }, [])

    const closeTooltip = useCallback(() => {
        clearHoverTimer()
        setShowTooltip(false)
        rectangleRef.current?.closeTooltip?.()
    }, [clearHoverTimer])

    const getTooltipDirection = useCallback((latlng) => {
        if (!map || !latlng) return 'top'
        const point = map.latLngToContainerPoint(latlng)
        const size = map.getSize()
        if (point.y < 170) return 'bottom'
        if (size.x - point.x < 300) return 'left'
        if (point.x < 300) return 'right'
        return 'top'
    }, [map])

    const scheduleTooltip = useCallback((event) => {
        if (!interactive) return
        lastLatLngRef.current = event.latlng
        setTooltipDirection(getTooltipDirection(event.latlng))
        clearHoverTimer()
        hoverTimerRef.current = setTimeout(() => {
            setShowTooltip(true)
            hoverTimerRef.current = null
        }, HEATMAP_TOOLTIP_DELAY_MS)
    }, [clearHoverTimer, getTooltipDirection, interactive])

    const updateTooltipPosition = useCallback((event) => {
        if (!interactive) return
        lastLatLngRef.current = event.latlng
        setTooltipDirection(getTooltipDirection(event.latlng))
        if (showTooltip) {
            rectangleRef.current?.openTooltip?.(event.latlng)
        }
    }, [getTooltipDirection, interactive, showTooltip])

    useEffect(() => () => {
        clearHoverTimer()
        rectangleRef.current?.closeTooltip?.()
    }, [clearHoverTimer])

    useEffect(() => {
        if (!showTooltip || !interactive) return undefined
        const openTimer = setTimeout(() => {
            rectangleRef.current?.openTooltip?.(lastLatLngRef.current || undefined)
        }, 0)
        return () => clearTimeout(openTimer)
    }, [interactive, showTooltip])

    return (
        <Rectangle
            ref={rectangleRef}
            key={cellKey}
            bounds={bounds}
            pathOptions={pathOptions}
            interactive={interactive}
            eventHandlers={interactive ? {
                mouseover: scheduleTooltip,
                mousemove: updateTooltipPosition,
                mouseout: closeTooltip,
                remove: closeTooltip
            } : undefined}
        >
            {showTooltip && interactive && (
                <Tooltip
                    sticky
                    direction={tooltipDirection}
                    opacity={1}
                    className="heatmap-cell-tooltip"
                >
                    <HeatmapCellTooltipContent
                        cell={cell}
                        metadata={metadata}
                        metricKey={metricKey}
                        metricValue={metricValue}
                    />
                </Tooltip>
            )}
        </Rectangle>
    )
}

const HEATMAP_METRIC_LABELS = {
    raw: 'Total Catch',
    cpue: 'Relative Abundance',
    biomass_cpue: 'Relative Biomass',
    effort: 'Effort',
    encounter_rate: 'Encounter Rate'
}

const MAP_RESIZE_STEP_PX = 24
const MIN_MAP_HEIGHT_PX = 280
const MIN_LOWER_PANEL_HEIGHT_PX = 180
const MAP_RESIZE_VIEWPORT_CHROME_PX = 150

function MapView () {
    // Add map reference
    const mapRef = useRef(null);
    const filterRequestAbortRef = useRef(null)
    const pointRequestAbortRef = useRef(null)
    const basemapMenuRef = useRef(null)
    const overlayScrollWheelPauseRef = useRef({
        active: false,
        map: null,
        wasEnabled: false
    })
    const isAbortError = useCallback((error) => error?.name === 'AbortError', [])
    const returnLinkConfig = useMemo(() => getReturnLinkConfig(), [])

    const abortFilterRequest = useCallback(() => {
        filterRequestAbortRef.current?.abort()
        filterRequestAbortRef.current = null
    }, [])

    const beginFilterRequest = useCallback(() => {
        abortFilterRequest()
        const controller = new AbortController()
        filterRequestAbortRef.current = controller
        return controller
    }, [abortFilterRequest])

    const isCurrentFilterRequest = useCallback((controller) => (
        filterRequestAbortRef.current === controller && !controller.signal.aborted
    ), [])

    const abortPointRequest = useCallback(() => {
        pointRequestAbortRef.current?.abort()
        pointRequestAbortRef.current = null
    }, [])

    const beginPointRequest = useCallback(() => {
        abortPointRequest()
        const controller = new AbortController()
        pointRequestAbortRef.current = controller
        return controller
    }, [abortPointRequest])

    const isCurrentPointRequest = useCallback((controller) => (
        pointRequestAbortRef.current === controller && !controller.signal.aborted
    ), [])

    useEffect(() => () => {
        abortFilterRequest()
        abortPointRequest()
    }, [abortFilterRequest, abortPointRequest])

    // Initial tab respects a `?section=` (or `?tab=`) URL param so other site
    // pages can deep-link into a specific WebMap section. Recognized values:
    //   trawl | trawlstations | trawl-bio | trawlbiology
    //   longline | longlinestations | longline-bio | longlinebiology
    //   plankton | planktonstations | plankton-bio | planktonbiology
    const [activeTab, setActiveTab] = useState(() => {
        if (typeof window === 'undefined') return 'TrawlStations'
        const params = new URLSearchParams(window.location.search)
        const raw = (params.get('section') || params.get('tab') || '').toLowerCase().replace(/[\s_-]/g, '')
        switch (raw) {
            case 'longline':
            case 'longlinestations':
                return 'LonglineStations'
            case 'longlinebio':
            case 'longlinebiology':
                return 'LonglineBiology'
            case 'plankton':
            case 'planktonstations':
                return PLANKTON_WEBMAP_ENABLED ? 'PlanktonStations' : 'TrawlStations'
            case 'planktonbio':
            case 'planktonbiology':
                return PLANKTON_WEBMAP_ENABLED ? 'PlanktonBiology' : 'TrawlStations'
            case 'trawlbio':
            case 'trawlbiology':
                return 'TrawlBiology'
            case 'trawl':
            case 'trawlstations':
            default:
                return 'TrawlStations'
        }
    })
    const [filteredData, setFilteredData] = useState([])
    const [currentQueryContext, setCurrentQueryContext] = useState(null)
    const [mapDisplayMode, setMapDisplayMode] = useState('markers')
    const [heatmapMetric, setHeatmapMetric] = useState('raw')
    const [biologyHeatmapGrid, setBiologyHeatmapGrid] = useState(null)
    const [biologyHeatmapLoading, setBiologyHeatmapLoading] = useState(false)
    const [biologyHeatmapError, setBiologyHeatmapError] = useState('')
    const [isLegendPanelCollapsed, setIsLegendPanelCollapsed] = useState(false)
    const [isDetailsPanelCollapsed, setIsDetailsPanelCollapsed] = useState(true)
    const [activeBasemapId, setActiveBasemapId] = useState('imagery')
    const [basemapMenuOpen, setBasemapMenuOpen] = useState(false)
    const [areaSelectionEnabled, setAreaSelectionEnabled] = useState(false)
    const [areaSelectionPreview, setAreaSelectionPreview] = useState(null)
    const [areaSelectionDialogOpen, setAreaSelectionDialogOpen] = useState(false)
    const [areaSelectionData, setAreaSelectionData] = useState(null)
    const [areaSelectionLoading, setAreaSelectionLoading] = useState(false)
    const [areaSelectionError, setAreaSelectionError] = useState('')
    const [longlineSpecies, setLonglineSpecies] = useState([])
    const [trawlSpecies, setTrawlSpecies] = useState([])
    const [planktonSpecies, setPlanktonSpecies] = useState([])

    const [selectedState, setSelectedState] = useState([])
    const [selectedYear, setSelectedYear] = useState([])
    const [selectedSpecies, setSelectedSpecies] = useState([])
    const [selectedSeason, setSelectedSeason] = useState('')
    const [selectedPointData, setSelectedPointData] = useState(null)

    // Optional date range. Both fields are independent of the year multiselect:
    // the year picker still drives the backend fetch (so the API contract is
    // unchanged), and these dates narrow the already-fetched rows client-side
    // via applyDateRange() at every setFilteredData(validData) site below.
    // Empty string = unset. Format from <input type="date"> is 'yyyy-mm-dd'.
    const [dateRangeStart, setDateRangeStart] = useState('')
    const [dateRangeEnd, setDateRangeEnd] = useState('')

    // State for the trawl shapefile layer
    const [showTrawlShapefile, setShowTrawlShapefile] = useState(false)

    // State for the historic trawl shapefile layer
    const [showHistoricTrawlShapefile, setShowHistoricTrawlShapefile] = useState(false)

    // State for the Bottom Longline shapefile layers
    const [showLongline2008Shapefile, setShowLongline2008Shapefile] = useState(false)
    const [showLongline2015Shapefile, setShowLongline2015Shapefile] = useState(false)
    const [showLongline2024Shapefile, setShowLongline2024Shapefile] = useState(false)

    const [snackbarOpen, setSnackbarOpen] = useState(false)
    const [snackbarMessage, setSnackbarMessage] = useState('')

    // State for loading indicator
    const [isLoading, setIsLoading] = useState(false)



    // Ref to track if we should force bounds fitting for points
    const shouldFitBoundsRef = useRef(false)

    // State for filter collapse functionality
    const [isFilterCollapsed, setIsFilterCollapsed] = useState(false)
    const shouldCollapseFilterAfterApply = useMediaQuery('(max-width:899px)')
    const isMobileBasemapMenu = useMediaQuery('(max-width:600px)')

    const pauseMapScrollWheelForOverlay = useCallback(() => {
        const map = mapRef.current
        const scrollWheelZoom = map?.scrollWheelZoom
        if (!scrollWheelZoom || typeof scrollWheelZoom.enabled !== 'function') return

        const pauseState = overlayScrollWheelPauseRef.current
        if (pauseState.active && pauseState.map === map) return

        pauseState.active = true
        pauseState.map = map
        pauseState.wasEnabled = scrollWheelZoom.enabled()

        if (pauseState.wasEnabled && typeof scrollWheelZoom.disable === 'function') {
            scrollWheelZoom.disable()
        }
    }, [])

    const restoreMapScrollWheelAfterOverlay = useCallback(() => {
        const pauseState = overlayScrollWheelPauseRef.current
        if (!pauseState.active) return

        const map = pauseState.map
        const scrollWheelZoom = map?.scrollWheelZoom
        try {
            if (
                pauseState.wasEnabled &&
                scrollWheelZoom &&
                typeof scrollWheelZoom.enabled === 'function' &&
                typeof scrollWheelZoom.enable === 'function' &&
                !scrollWheelZoom.enabled()
            ) {
                scrollWheelZoom.enable()
            }
        } finally {
            pauseState.active = false
            pauseState.map = null
            pauseState.wasEnabled = false
        }
    }, [])

    useEffect(() => restoreMapScrollWheelAfterOverlay, [restoreMapScrollWheelAfterOverlay])

    const getTabSection = (tab) => tab.includes('Plankton') ? 'Plankton' : (tab.includes('Longline') ? 'Longline' : 'Trawl')
    const getSurveyForTab = (tab) => tab.includes('Plankton') ? 'plankton' : (tab.includes('Longline') ? 'longline' : 'trawl')
    const isAllLike = (value) => ['ALL', 'ALL YEARS', 'ALL SOURCES', '--Select--'].includes(value)

    const normalizeFilterArray = (value) => {
        if (Array.isArray(value)) {
            const cleaned = value
                .map(v => String(v || '').trim())
                .filter(v => v && !isAllLike(v))
            return cleaned.length > 0 ? cleaned : ['ALL']
        }
        const cleaned = String(value || '').trim()
        return cleaned && !isAllLike(cleaned) ? [cleaned] : ['ALL']
    }

    const buildQueryContext = (tab, sources, years) => ({
        survey: getSurveyForTab(tab),
        tab,
        filters: {
            sources: normalizeFilterArray(sources),
            years: normalizeFilterArray(years),
            season: tab === 'TrawlStations' || tab === 'TrawlBiology' || tab === 'PlanktonStations' || tab === 'PlanktonBiology'
                ? (selectedSeason || 'ALL')
                : 'ALL',
            species: Array.isArray(selectedSpecies)
                ? selectedSpecies.filter(sp => typeof sp === 'string' && sp.trim())
                : (selectedSpecies ? [selectedSpecies] : []),
            dateStart: dateRangeStart || '',
            dateEnd: dateRangeEnd || ''
        }
    })

    const appendDateRangeParams = (params) => {
        if (dateRangeStart) params.append('dateStart', dateRangeStart)
        if (dateRangeEnd) params.append('dateEnd', dateRangeEnd)
    }

    const boundsToPayload = (bounds) => {
        if (!bounds) return null
        const leafletBounds = typeof bounds.getNorth === 'function' ? bounds : L.latLngBounds(bounds)
        return {
            north: leafletBounds.getNorth(),
            south: leafletBounds.getSouth(),
            east: leafletBounds.getEast(),
            west: leafletBounds.getWest()
        }
    }

    const formatMapNumber = (value, digits = 2) => {
        const number = Number(value)
        return Number.isFinite(number) ? number.toFixed(digits) : 'n/a'
    }

    const getSweptAreaHectares = (item) => {
        const hectares = Number(item?.swept_area_hectares)
        if (Number.isFinite(hectares)) return hectares
        const km2 = Number(item?.swept_area_km2)
        return Number.isFinite(km2) ? km2 * 100 : null
    }

    const collapseFilterAfterApply = () => {
        if (shouldCollapseFilterAfterApply) {
            setIsFilterCollapsed(true)
        }
    }

    const resetTrawlZoneLayers = useCallback(() => {
        setShowTrawlShapefile(false)
        setShowHistoricTrawlShapefile(false)
    }, [])

    const resetLonglineZoneLayers = useCallback(() => {
        setShowLongline2008Shapefile(false)
        setShowLongline2015Shapefile(false)
        setShowLongline2024Shapefile(false)
    }, [])

    const recordWebmapFilterEvent = (resultCount) => {
        trackUsageEvent({
            event_type: 'webmap_filter',
            route_group: 'webmap',
            source_app: 'webmap',
            subject_type: 'webmap_tab',
            subject_id: activeTab,
            result_count: resultCount,
            metadata: {
                tab: activeTab,
                section: getTabSection(activeTab),
                view: activeTab.includes('Biology') ? 'Biology' : 'Stations',
                selectedSourceCount: Array.isArray(selectedState) ? selectedState.length : (selectedState ? 1 : 0),
                selectedYearCount: Array.isArray(selectedYear) ? selectedYear.length : (selectedYear ? 1 : 0),
                selectedSpeciesCount: Array.isArray(selectedSpecies) ? selectedSpecies.length : 0,
                selectedSeason: selectedSeason || null,
                hasDateRange: Boolean(dateRangeStart || dateRangeEnd)
            }
        })
    }

    useEffect(() => {
        trackUsageEvent({
            event_type: 'webmap_tab_view',
            route_group: 'webmap',
            source_app: 'webmap',
            subject_type: 'webmap_tab',
            subject_id: activeTab,
            metadata: {
                tab: activeTab,
                section: getTabSection(activeTab),
                view: activeTab.includes('Biology') ? 'Biology' : 'Stations'
            }
        })
    }, [activeTab])

    // Track whether the map area is still in view; hide the floating filter
    // overlay when the user scrolls past the map so it doesn't float over the
    // tables / footer below.
    const mapAreaRef = useRef(null)
    const resizeGripRef = useRef(null)
    const resultsBarRef = useRef(null)
    const returnNavRef = useRef(null)
    const tablePanelRef = useRef(null)
    const footerRef = useRef(null)
    const resizeStartRef = useRef(null)
    const resizeCleanupRef = useRef(null)
    const tableDragScrollRef = useRef(null)
    const suppressTableClickRef = useRef(false)
    const [isMapVisible, setIsMapVisible] = useState(true)
    const [customMapHeight, setCustomMapHeight] = useState(null)
    const [resizedTablePanelHeight, setResizedTablePanelHeight] = useState(null)
    const [isTableDragScrolling, setIsTableDragScrolling] = useState(false)

    const invalidateMapSize = useCallback(() => {
        window.requestAnimationFrame(() => {
            mapRef.current?.invalidateSize?.()
        })
    }, [])

    const getMapHeightLimits = useCallback(() => {
        if (typeof window === 'undefined') {
            return { min: MIN_MAP_HEIGHT_PX, max: 900 }
        }

        const availableHeight = window.innerHeight - MAP_RESIZE_VIEWPORT_CHROME_PX
        const max = Math.max(MIN_MAP_HEIGHT_PX + 80, availableHeight - MIN_LOWER_PANEL_HEIGHT_PX)
        return { min: MIN_MAP_HEIGHT_PX, max }
    }, [])

    const applyMapHeight = useCallback((nextHeight) => {
        const { min, max } = getMapHeightLimits()
        const clampedHeight = Math.min(max, Math.max(min, Math.round(nextHeight)))
        setCustomMapHeight(clampedHeight)
        invalidateMapSize()
    }, [getMapHeightLimits, invalidateMapSize])

    const resetMapHeight = useCallback(() => {
        setCustomMapHeight(null)
        invalidateMapSize()
    }, [invalidateMapSize])

    const adjustMapHeight = useCallback((delta) => {
        const currentHeight = customMapHeight || mapAreaRef.current?.getBoundingClientRect().height
        if (!currentHeight) return
        applyMapHeight(currentHeight + delta)
    }, [applyMapHeight, customMapHeight])

    const handleMapResizePointerDown = useCallback((event) => {
        if (!mapAreaRef.current) return

        event.preventDefault()
        resizeCleanupRef.current?.()
        const startHeight = mapAreaRef.current.getBoundingClientRect().height
        resizeStartRef.current = { startY: event.clientY, startHeight }
        document.body.style.cursor = 'row-resize'
        document.body.style.userSelect = 'none'

        const handlePointerMove = (moveEvent) => {
            if (!resizeStartRef.current) return
            const deltaY = moveEvent.clientY - resizeStartRef.current.startY
            applyMapHeight(resizeStartRef.current.startHeight + deltaY)
        }

        const handlePointerUp = () => {
            resizeStartRef.current = null
            document.body.style.cursor = ''
            document.body.style.userSelect = ''
            window.removeEventListener('pointermove', handlePointerMove)
            window.removeEventListener('pointerup', handlePointerUp)
            resizeCleanupRef.current = null
        }

        resizeCleanupRef.current = () => {
            resizeStartRef.current = null
            document.body.style.cursor = ''
            document.body.style.userSelect = ''
            window.removeEventListener('pointermove', handlePointerMove)
            window.removeEventListener('pointerup', handlePointerUp)
        }

        window.addEventListener('pointermove', handlePointerMove)
        window.addEventListener('pointerup', handlePointerUp)
    }, [applyMapHeight])

    const handleMapResizeKeyDown = useCallback((event) => {
        if (event.key === 'ArrowUp') {
            event.preventDefault()
            adjustMapHeight(-MAP_RESIZE_STEP_PX)
        } else if (event.key === 'ArrowDown') {
            event.preventDefault()
            adjustMapHeight(MAP_RESIZE_STEP_PX)
        } else if (event.key === 'Home') {
            event.preventDefault()
            applyMapHeight(getMapHeightLimits().min)
        } else if (event.key === 'End') {
            event.preventDefault()
            applyMapHeight(getMapHeightLimits().max)
        } else if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            resetMapHeight()
        }
    }, [adjustMapHeight, applyMapHeight, getMapHeightLimits, resetMapHeight])

    const updateResizedTablePanelHeight = useCallback(() => {
        if (
            typeof window === 'undefined' ||
            !selectedPointData ||
            !tablePanelRef.current
        ) {
            setResizedTablePanelHeight(null)
            return
        }

        const tableTop = tablePanelRef.current.getBoundingClientRect().top
        const availableHeight = window.innerHeight - tableTop
        const nextHeight = Math.max(MIN_LOWER_PANEL_HEIGHT_PX, Math.round(availableHeight))

        setResizedTablePanelHeight(prev => (
            Math.abs((prev || 0) - nextHeight) > 1 ? nextHeight : prev
        ))
    }, [selectedPointData])

    useEffect(() => {
        updateResizedTablePanelHeight()
    }, [customMapHeight, selectedPointData, filteredData.length, mapDisplayMode, updateResizedTablePanelHeight])

    useEffect(() => {
        window.addEventListener('resize', updateResizedTablePanelHeight)
        return () => window.removeEventListener('resize', updateResizedTablePanelHeight)
    }, [updateResizedTablePanelHeight])

    const resetTableDragScroll = useCallback(() => {
        tableDragScrollRef.current = null
        setIsTableDragScrolling(false)
        document.body.style.userSelect = ''
        document.body.style.cursor = ''
    }, [])

    const handleTablePanelPointerDown = useCallback((event) => {
        const panel = tablePanelRef.current
        if (!panel || !selectedPointData) return
        if (event.pointerType === 'mouse' && event.button !== 0) return
        if (event.target?.closest?.('a, button, input, select, textarea, [role="button"], [contenteditable="true"]')) return

        const canScroll = panel.scrollWidth > panel.clientWidth || panel.scrollHeight > panel.clientHeight
        if (!canScroll) return

        tableDragScrollRef.current = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            scrollLeft: panel.scrollLeft,
            scrollTop: panel.scrollTop,
            moved: false
        }
        panel.setPointerCapture?.(event.pointerId)
        document.body.style.userSelect = 'none'
    }, [selectedPointData])

    const handleTablePanelPointerMove = useCallback((event) => {
        const dragState = tableDragScrollRef.current
        const panel = tablePanelRef.current
        if (!dragState || !panel || dragState.pointerId !== event.pointerId) return

        const deltaX = event.clientX - dragState.startX
        const deltaY = event.clientY - dragState.startY
        if (!dragState.moved && Math.hypot(deltaX, deltaY) < 4) return

        event.preventDefault()
        dragState.moved = true
        setIsTableDragScrolling(true)
        document.body.style.cursor = 'grabbing'
        panel.scrollLeft = dragState.scrollLeft - deltaX
        panel.scrollTop = dragState.scrollTop - deltaY
    }, [])

    const handleTablePanelPointerEnd = useCallback((event) => {
        const dragState = tableDragScrollRef.current
        if (!dragState || dragState.pointerId !== event.pointerId) return
        if (dragState.moved) {
            suppressTableClickRef.current = true
            window.setTimeout(() => {
                suppressTableClickRef.current = false
            }, 0)
        }
        resetTableDragScroll()
    }, [resetTableDragScroll])

    const handleTablePanelClickCapture = useCallback((event) => {
        if (!suppressTableClickRef.current) return
        event.preventDefault()
        event.stopPropagation()
        suppressTableClickRef.current = false
    }, [])

    useEffect(() => resetTableDragScroll, [resetTableDragScroll])
    useEffect(() => {
        if (!selectedPointData) resetTableDragScroll()
    }, [resetTableDragScroll, selectedPointData])

    useEffect(() => {
        const HEADER_OFFSET = 116
        const update = () => {
            if (!mapAreaRef.current) return
            const rect = mapAreaRef.current.getBoundingClientRect()
            setIsMapVisible(rect.bottom > HEADER_OFFSET)
        }
        update()
        window.addEventListener('scroll', update, { passive: true })
        window.addEventListener('resize', update)
        return () => {
            window.removeEventListener('scroll', update)
            window.removeEventListener('resize', update)
        }
    }, [])

    useEffect(() => {
        if (customMapHeight !== null) invalidateMapSize()
    }, [customMapHeight, invalidateMapSize])

    useEffect(() => () => {
        resizeCleanupRef.current?.()
        resizeCleanupRef.current = null
    }, [])

    // State for controlling dropdown open/close
    const [speciesDropdownOpen, setSpeciesDropdownOpen] = React.useState(false);
    const [yearDropdownOpen, setYearDropdownOpen] = React.useState(false);



    // hardcode the states and years - sorted alphabetically
    const trawlStates = ['AL', 'FL', 'LA', 'MS', 'NMFS', 'TX']
    const longlineStates = ['AL', 'FL', 'LA', 'MS', 'TX']
    const planktonStates = ['AL', 'FL', 'LA', 'MS', 'NMFS']

    const currentYear = new Date().getFullYear()
    const trawlYears = []
    const longlineYears = []
    const planktonYears = []

    // Trawl years: current to 1982 (descending order for better UX)
    for (let y = currentYear; y >= 1982; y--) {
        trawlYears.push(y)
    }

    // Longline years: current to 2007 (descending order for better UX)
    for (let y = currentYear; y >= 2007; y--) {
        longlineYears.push(y)
    }

    // Plankton years: current to 1982.
    for (let y = currentYear; y >= 1982; y--) {
        planktonYears.push(y)
    }

    // Get the appropriate states and years based on active tab
    const getStatesForTab = () => {
        if (activeTab.includes('Plankton')) {
            return planktonStates
        }
        if (activeTab.includes('Longline')) {
            return longlineStates
        }
        return trawlStates
    }

    const getYearsForTab = () => {
        if (activeTab.includes('Plankton')) {
            return planktonYears
        }
        if (activeTab.includes('Longline')) {
            return longlineYears
        }
        return trawlYears
    }

        // Load and parse Longline species data (current names only)
    useEffect(() => {
        const loadLonglineSpecies = async () => {
            try {
                const response = await fetch(bllTaxons)
                const text = await response.text()
                const rows = text.split('\n').slice(1) // Skip header row
                const speciesList = rows
                    .map(row => {
                        // Handle CSV format: GENUS,SPECIES,TAXON,BIOCODE,CIU_BIOCODE,IS_CURRENT,INACTIVE
                        const columns = row.split(',')
                        if (columns.length < 6) return null

                        const genus = columns[0]?.replace(/['"]/g, '').trim()
                        const species = columns[1]?.replace(/['"]/g, '').trim()
                        const isCurrent = columns[5]?.replace(/['"]/g, '').trim()
                        const inactive = columns[6]?.replace(/['"]/g, '').trim()

                        // Only include active, current species
                        if (isCurrent !== 'true' || inactive === '1') {
                            return null
                        }

                        // Skip entries with no genus or species
                        if (!genus || !species) {
                            return null
                        }

                        // Return the full GENUS SPECIES format
                        return `${genus} ${species}`
                    })
                    .filter(item => item !== null)
                    .sort()


                setLonglineSpecies(speciesList)
            } catch (error) {
                console.error('Error loading longline species:', error)
                setLonglineSpecies([])
            }
        }
        loadLonglineSpecies()
    }, [])

    // Load and parse Trawl species data
    useEffect(() => {
        const loadTrawlSpecies = async () => {
            try {
                const response = await fetch(trawlTaxons)
                const text = await response.text()
                const rows = text.split('\n').slice(1) // Skip header row
                const speciesList = rows
                    .map(row => row.trim())
                    .filter(row => row !== '') // Only filter out empty rows
                    .sort()


                setTrawlSpecies(speciesList)
            } catch (error) {
                console.error('Error loading Trawl species:', error)
                setTrawlSpecies([])
            }
        }
        loadTrawlSpecies()
    }, [])

    useEffect(() => {
        const loadPlanktonSpecies = async () => {
            try {
                const response = await fetch(`${API_URL}/plankton-taxa`)
                if (!response.ok) {
                    throw new Error(`Plankton taxa request failed with ${response.status}`)
                }
                const taxa = await response.json()
                setPlanktonSpecies(Array.isArray(taxa) ? taxa.filter(Boolean).sort() : [])
            } catch (error) {
                console.error('Error loading Plankton taxa:', error)
                setPlanktonSpecies([])
            }
        }
        loadPlanktonSpecies()
    }, [])

    // Modify the handleTabClick function to reset the map view
    const handleTabClick = (tab) => {
        const currentSection = getTabSection(activeTab)
        const nextSection = getTabSection(tab)

        setActiveTab(tab)
        setFilteredData([])        // clears point data
        setCurrentQueryContext(null)
        setMapDisplayMode('markers')
        setHeatmapMetric('raw')
        setBiologyHeatmapGrid(null)
        setBiologyHeatmapError('')
        setAreaSelectionEnabled(false)
        setAreaSelectionPreview(null)
        setAreaSelectionDialogOpen(false)
        setSelectedPointData(null) // clears table

        setIsFilterCollapsed(false) // reset filter collapse state when switching tabs

        // Reset map view if the map reference is available
        if (mapRef.current) {
            try {
                mapRef.current.setView([28.6, -89], 7, {
                    animate: true,
                    duration: 0.75
                });
            } catch (err) {
                console.error('Error resetting map view:', err);
            }
        } else {
            console.warn('Map reference not available for reset');
        }

        // Set selectedState as empty array specifically for stations tabs
        if (tab === 'TrawlStations' || tab === 'LonglineStations' || tab === 'PlanktonStations') {
          setSelectedState([])     // empty array for multi-select
          setSelectedYear([])      // empty array for multi-select
        } else {
          setSelectedState('')     // empty string for other tabs
          setSelectedYear('')      // empty string for other tabs
        }

        setSelectedSpecies([])
        setSelectedSeason('')      // reset season

        // Reset dropdown states
        setSpeciesDropdownOpen(false)
        setYearDropdownOpen(false)

        if (currentSection !== nextSection) {
            if (currentSection === 'Trawl') {
                resetTrawlZoneLayers()
            } else {
                resetLonglineZoneLayers()
            }
        }
    }

    // Add useEffect to ensure points are cleared when tab changes
    useEffect(() => {
        abortFilterRequest()
        abortPointRequest()
        // Clear filtered data when tab changes
        setFilteredData([])
        setCurrentQueryContext(null)
        setMapDisplayMode('markers')
        setHeatmapMetric('raw')
        setBiologyHeatmapGrid(null)
        setBiologyHeatmapError('')
        setIsLoading(false)
    }, [activeTab, abortFilterRequest, abortPointRequest])

    // Utility to append species to URLSearchParams, supporting single, array, and JSON-array string
    const appendSpeciesParams = (params) => {
        if (Array.isArray(selectedSpecies)) {
            const joined = selectedSpecies
                .filter(sp => typeof sp === 'string' && sp.trim())
                .join(',');
            if (joined) params.append('species', joined);
        } else if (typeof selectedSpecies === 'string') {
            const trimmed = selectedSpecies.trim();
            if (!trimmed) return;
            params.append('species', trimmed);
        }
    }

    // handle point click based on active tab
    const handleMarkerClick = (item) => {
        // Ensure we don't trigger bounds fitting when clicking points
        shouldFitBoundsRef.current = false;

        // Show loading indicator
        setIsLoading(true)

        const controller = beginPointRequest()
        let endpoint, params

        if (activeTab === 'TrawlStations') {
            endpoint = '/trawl-point'
            params = new URLSearchParams({ stationid: item.stationid })
        }
        else if (activeTab === 'LonglineStations') {
            endpoint = '/longline-point'
            params = new URLSearchParams({
                stationid: item.stationid,
                sid: item.sid
            })
        }
        else if (activeTab === 'PlanktonStations') {
            endpoint = '/plankton-point'
            params = new URLSearchParams({ stationid: item.stationid || item.v_c_p })
        }
        else if (activeTab === 'TrawlBiology') {
            endpoint = '/trawl-biology-point'
            // Prefer the matched taxon for the clicked point to ensure correct point data
            const matchedForPoint = item.matched_taxon || ''
            const fallbackSelected = Array.isArray(selectedSpecies) ? (selectedSpecies[0] || '') : selectedSpecies
            const speciesForPoint = matchedForPoint || fallbackSelected
            params = new URLSearchParams({
                stationid: item.stationid,
                species: speciesForPoint
            })
        }
        else if (activeTab === 'LonglineBiology') {
            endpoint = '/longline-biology-point'
            // Prefer the matched species name for the clicked point to ensure correct point data
            const matchedForPoint = item.matched_species || ''
            const fallbackSelected = Array.isArray(selectedSpecies) ? (selectedSpecies[0] || '') : selectedSpecies
            const speciesForPoint = matchedForPoint || fallbackSelected
            params = new URLSearchParams({
                stationid: item.stationid,
                species: speciesForPoint
            })
        }
        else if (activeTab === 'PlanktonBiology') {
            endpoint = '/plankton-biology-point'
            const speciesForPoint = item.ciu_taxon || item.CIU_TAXON || item.matched_taxon || (Array.isArray(selectedSpecies) ? (selectedSpecies[0] || '') : selectedSpecies)
            params = new URLSearchParams({
                stationid: item.stationid || item.v_c_p,
                species: speciesForPoint
            })
        }
        else {
            // For other tabs or future implementation
            setIsLoading(false)
            return
        }

        const requestUrl = `${API_URL}${endpoint}?${params.toString()}`

        // Make the API request
        fetch(requestUrl, { signal: controller.signal })
            .then(response => {
                if (!response.ok) {
                    console.error('API error:', response.statusText);
                    throw new Error(`API returned ${response.status}: ${response.statusText}`);
                }
                return response.json();
            })
            .then(data => {
                if (!isCurrentPointRequest(controller)) return
                if (data.length === 0) {
                    // Only show message for Trawl tabs
                    if (!activeTab.includes('Longline')) {
                        showNoResultsMessage()
                    }
                    setIsLoading(false)
                    return
                }

                setSelectedPointData(data)
                setIsLoading(false)
            })
            .catch(error => {
                if (isAbortError(error)) return
                if (!isCurrentPointRequest(controller)) return
                console.error('Error fetching point data:', error)
                // Only show error for Trawl tabs
                if (!activeTab.includes('Longline')) {
                    showNoResultsMessage()
                }
                setIsLoading(false)
            })
    }

    // Function to show "no results" message
    const showNoResultsMessage = () => {
        // Skip all messages for Bottom Longline tabs
        if (activeTab.includes('Longline')) {
            return;
        }
        setSnackbarMessage("No records matched these filters. Try a broader year, source, season, or date range.")
        setSnackbarOpen(true)
    }

    // Handle close action for snackbar
    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return
        }
        setSnackbarOpen(false)
    }

    // handler for filter button - Stations tabs
    // Best-effort date extraction across the schemas the WebMap pulls from.
    // Returns null when no usable date is present so the caller can decide
    // whether to drop the row (we drop it only when a date range is active).
    //   - TrawlBiology / LonglineBiology: SAMPLE_DATE        (DB date string)
    //   - LonglineStations:               date               (DB date, formatted YYYY-MM-DD)
    //   - TrawlStations:                  mo_day_yr          (formatted YYYY-MM-DD or 'MM/DD/YYYY')
    //   - TrawlStations fallback:         startcru           (cruise start date, YYYY-MM-DD)
    //   - Legacy:                         mo / day / yr      (split fields)
    const getRowDate = (row) => {
        if (!row) return null
        const tryParse = (val) => {
            if (val === null || val === undefined || val === '') return null
            // Native Date / numeric epoch
            if (val instanceof Date) return isNaN(val.getTime()) ? null : val
            const s = String(val).trim()
            // ISO-ish (YYYY-MM-DD or full ISO) — let Date parse it
            if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
                const d = new Date(s)
                if (!isNaN(d.getTime())) return d
            }
            // US-style MM/DD/YYYY
            if (s.includes('/')) {
                const [m, d, y] = s.split('/').map(Number)
                if (m && d && y) return new Date(y, m - 1, d)
            }
            // Last resort — let Date try
            const d = new Date(s)
            return isNaN(d.getTime()) ? null : d
        }
        return tryParse(row.SAMPLE_DATE)
            || tryParse(row.date)
            || tryParse(row.mo_day_yr)
            || tryParse(row.startcru)
            || (row.mo && row.day && row.yr
                ? new Date(Number(row.yr), Number(row.mo) - 1, Number(row.day))
                : null)
    }

    // Narrow a row set by the optional date range. No-op when both fields are
    // empty. Endpoints from <input type="date"> are local-time inclusive.
    // Rows whose date can't be parsed are dropped only when the range is
    // active (so the filter doesn't silently swallow rows under year-only mode).
    const applyDateRange = (rows) => {
        if (!dateRangeStart && !dateRangeEnd) return rows
        const start = dateRangeStart ? new Date(`${dateRangeStart}T00:00:00`) : null
        const end = dateRangeEnd ? new Date(`${dateRangeEnd}T23:59:59.999`) : null
        return rows.filter(row => {
            const d = getRowDate(row)
            if (!d) return false
            if (start && d < start) return false
            if (end && d > end) return false
            return true
        })
    }

    // Derive a fetchable year list from the optional date range so a user can
    // submit "date range only" without explicitly picking years. Both bounds
    // present → return the inclusive year span as strings (so the multi-year
    // branch fans out one request per year). Only one bound present → fall
    // back to ['ALL YEARS'] because we don't know the open end of the span;
    // the client-side applyDateRange() still narrows the result correctly.
    const deriveYearsFromRange = (start, end) => {
        const startYear = start ? new Date(`${start}T00:00:00`).getFullYear() : null
        const endYear = end ? new Date(`${end}T00:00:00`).getFullYear() : null
        if (startYear && endYear && endYear >= startYear) {
            const out = []
            for (let y = startYear; y <= endYear; y++) out.push(String(y))
            return out
        }
        return ['ALL YEARS']
    }

    // Wrap setFilteredData so the optional date range is applied uniformly at
    // every call site. If the range filters everything out, surface a snackbar
    // hint so the user understands why the map went empty.
    const setFilteredDataWithRange = (rows) => {
        const narrowed = applyDateRange(rows)
        if (rows.length > 0 && narrowed.length === 0 && (dateRangeStart || dateRangeEnd)) {
            setSnackbarMessage('No records fall within the selected date range.')
            setSnackbarOpen(true)
        }
        setFilteredData(narrowed)
    }

    const handleStationsFilter = () => {
        // Check if this is a trawl stations tab
        const isTrawlStationsTab = activeTab === 'TrawlStations';

        // Check if this is a longline stations tab
        const isLonglineStationsTab = activeTab === 'LonglineStations';
        const isPlanktonStationsTab = activeTab === 'PlanktonStations';

        // Validate selections for stations tabs. Source and Year are each
        // optional individually as long as the user has supplied SOMETHING the
        // request can be bounded by — Source, Year, or a Date Range. When
        // Source or Year is omitted but a Date Range is set, we substitute
        // 'ALL' for the missing dimension and rely on applyDateRange() to
        // narrow the response client-side.
        const hasDateRange = Boolean(dateRangeStart || dateRangeEnd);
        let effectiveState;
        let effectiveYear;
        if (isTrawlStationsTab || isLonglineStationsTab || isPlanktonStationsTab) {
            const hasValidState = Array.isArray(selectedState) && selectedState.length > 0 &&
                                 !selectedState.every(state => state === '--Select--');
            const hasValidYear = Array.isArray(selectedYear) && selectedYear.length > 0 &&
                                !selectedYear.every(year => year === '--Select--');

            if (!hasValidState && !hasValidYear && !hasDateRange) {
                setSnackbarMessage("Please select Source, Year, or a Date Range before filtering.");
                setSnackbarOpen(true);
                return;
            }

            effectiveState = hasValidState ? selectedState : ['ALL SOURCES'];
            effectiveYear = hasValidYear ? selectedYear : deriveYearsFromRange(dateRangeStart, dateRangeEnd);
        } else {
            const hasValidStateSingle = selectedState && selectedState !== '--Select--';
            const hasValidYearSingle = selectedYear && selectedYear !== '--Select--';

            if (!hasValidStateSingle && !hasValidYearSingle && !hasDateRange) {
                setSnackbarMessage("Please select Source, Year, or a Date Range before filtering.");
                setSnackbarOpen(true);
                return;
            }

            effectiveState = hasValidStateSingle ? selectedState : 'ALL';
            effectiveYear = hasValidYearSingle ? selectedYear : deriveYearsFromRange(dateRangeStart, dateRangeEnd);
        }

        // Show loading message for potentially large datasets
        if (!isTrawlStationsTab &&
            !activeTab.includes('Longline') && // Add condition to check if it's not a Longline tab
            (Array.isArray(effectiveState) && (effectiveState.includes("ALL SOURCES") || effectiveState.length === 0)) &&
            (Array.isArray(effectiveYear) && (effectiveYear.includes("ALL YEARS") || effectiveYear.length === 0))) {
            setSnackbarMessage("Loading all stations with clustering... This may take a moment for large datasets.")
            setSnackbarOpen(true)
        }

        // Reset data
        setFilteredData([])
        setSelectedPointData(null)


        // Set flag to force map to fit bounds to points when they're loaded
        shouldFitBoundsRef.current = true

        // Show loading indicator
        setIsLoading(true)
        const controller = beginFilterRequest()

        // Determine the endpoint based on the active tab
        let endpoint
        switch (activeTab) {
            case 'TrawlStations':
                endpoint = '/trawl-state-year'
                break
            case 'LonglineStations':
                endpoint = '/longline-state-year'
                break
            case 'PlanktonStations':
                endpoint = '/plankton-state-year'
                break
            default:
                endpoint = '/trawl-state-year'
        }

        // Create state parameter (uses effectiveState so a date-range-only
        // submission gets a sensible 'ALL' fallback for the missing dimension).
        let stateParam;
        if (Array.isArray(effectiveState)) {
            if (effectiveState.includes("ALL SOURCES")) {
                stateParam = "ALL";
            } else {
                stateParam = effectiveState.join(',');
            }
        } else {
            stateParam = effectiveState === "ALL" ? "ALL" : effectiveState;
        }

        // Create year parameter (uses effectiveYear, which falls back to a
        // year span derived from the date range when Year is left blank).
        let yearParam;
        if (Array.isArray(effectiveYear)) {
            if (effectiveYear.includes("ALL YEARS")) {
                yearParam = "ALL";
            } else {
                yearParam = effectiveYear.join(',');
            }
        } else {
            yearParam = effectiveYear === "ALL" ? "ALL" : effectiveYear;
        }

        // Check if we need to make multiple requests for different years
        const yearValues = Array.isArray(effectiveYear) ?
            (effectiveYear.includes("ALL YEARS") ? ["ALL"] : effectiveYear) :
            [effectiveYear === "ALL" ? "ALL" : effectiveYear];
        const requestContext = buildQueryContext(activeTab, effectiveState, effectiveYear);

        if (yearValues.length > 1 && !yearValues.includes("ALL")) {
            // Multiple year requests
            const requests = yearValues.map(year => {
                const params = new URLSearchParams();
                params.append('state', stateParam);
                params.append('year', year);
                appendDateRangeParams(params);

                // Add season parameter where supported
                if ((isTrawlStationsTab || isPlanktonStationsTab) && selectedSeason && selectedSeason !== 'ALL') {
                    params.append('season', selectedSeason);
                }

                const requestUrl = `${API_URL}${endpoint}?${params.toString()}`;

                return fetch(requestUrl, { signal: controller.signal })
                    .then(response => {
                        if (!response.ok) {
                            throw new Error(`HTTP error! status: ${response.status}`);
                        }
                        return response.json();
                    });
            });

            Promise.all(requests)
                .then(results => {
                    if (!isCurrentFilterRequest(controller)) return

                    // Combine all results
                    const combinedData = results.flat();

                    // Filter out invalid coordinates
                    const validData = combinedData.filter(item => {
                        const lat = parseFloat(item.decslat);
                        const lon = parseFloat(item.decslon);
                        return !isNaN(lat) && !isNaN(lon) &&
                               lat !== 0 && lon !== 0 &&
                               lat >= -90 && lat <= 90 &&
                               lon >= -180 && lon <= 180;
                    });

                    // Check if any stations were filtered out due to invalid coordinates
                    if (validData.length < combinedData.length && activeTab !== 'TrawlStations' && !activeTab.includes('Longline')) {
                        setSnackbarMessage("Not all results contain valid coordinates, these are not displayed.");
                        setSnackbarOpen(true);
                    }

                    setCurrentQueryContext(requestContext);
                    setFilteredDataWithRange(validData);

                    recordWebmapFilterEvent(validData.length);

                    // Collapse the filter after successful query on mobile only.
                    collapseFilterAfterApply();
                    setIsLoading(false);
                })
                .catch(error => {
                    if (isAbortError(error)) return
                    if (!isCurrentFilterRequest(controller)) return
                    console.error('Error processing multi-year requests:', error);
                    // Only show error for Trawl
                    if (!activeTab.includes('Longline')) {
                        showNoResultsMessage();
                    }
                    setIsLoading(false);
                });
        } else {
            // Single year request - standard approach
            const params = new URLSearchParams();
            params.append('state', stateParam);
            params.append('year', yearParam);
            appendDateRangeParams(params);

            // Add season parameter where supported
            if ((isTrawlStationsTab || isPlanktonStationsTab) && selectedSeason && selectedSeason !== 'ALL') {
                params.append('season', selectedSeason);
            }

            const requestUrl = `${API_URL}${endpoint}?${params.toString()}`;

            fetch(requestUrl, { signal: controller.signal })
                .then(response => {
                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }
                    return response.json();
                })
                .then(data => {
                    if (!isCurrentFilterRequest(controller)) return

                    // Filter out invalid coordinates
                    const validData = data.filter(item => {
                        const lat = parseFloat(item.decslat);
                        const lon = parseFloat(item.decslon);
                        return !isNaN(lat) && !isNaN(lon) &&
                               lat !== 0 && lon !== 0 &&
                               lat >= -90 && lat <= 90 &&
                               lon >= -180 && lon <= 180;
                    });

                    // Check if any stations were filtered out due to invalid coordinates
                    if (validData.length < data.length && activeTab !== 'TrawlStations' && !activeTab.includes('Longline')) {
                        setSnackbarMessage("Not all results contain valid coordinates, these are not displayed.");
                        setSnackbarOpen(true);
                    }

                    if (validData.length === 0) {
                        showNoResultsMessage();
                        setIsLoading(false);
                    } else {
                        setCurrentQueryContext(requestContext);
                        setFilteredDataWithRange(validData);

                        recordWebmapFilterEvent(validData.length);

                        // Collapse the filter after successful query on mobile only.
                        collapseFilterAfterApply();
                        setIsLoading(false);
                    }
                })
                .catch(error => {
                    if (isAbortError(error)) return
                    if (!isCurrentFilterRequest(controller)) return
                    console.error('Error fetching data:', error);
                    showNoResultsMessage();
                    setIsLoading(false);
                });
        }
    }

    // Handle biology filter button click
    const handleBiologyFilter = () => {
        // Reset selected point data and clear existing points
        setSelectedPointData(null)
        setFilteredData([])

        // Reset species color assignments for new query
        resetSpeciesColors()

        // Set flag to force map to fit bounds to points when they're loaded
        shouldFitBoundsRef.current = true

        // Validate inputs. Species is the foundational input for biology
        // queries — without it the backend has nothing to scope against. Year
        // is optional and may be substituted by the date range below.
        if (!Array.isArray(selectedSpecies) || selectedSpecies.length === 0) {
            // Only show error messages for Trawl Biology
            if (activeTab === 'TrawlBiology') {
                setSnackbarMessage("Please select a species.")
                setSnackbarOpen(true)
            }
            return
        }

        // When Year is left blank but a Date Range is supplied, derive a year
        // list from the range so the existing year-fanout logic still works.
        const hasValidYearBio = Array.isArray(selectedYear)
            ? (selectedYear.length > 0 && !selectedYear.every(year => year === '--Select--'))
            : Boolean(selectedYear) && selectedYear !== '--Select--'
        const hasDateRangeBio = Boolean(dateRangeStart || dateRangeEnd)
        const effectiveYearBio = hasValidYearBio
            ? selectedYear
            : (hasDateRangeBio ? deriveYearsFromRange(dateRangeStart, dateRangeEnd) : selectedYear)
        const requestContext = buildQueryContext(activeTab, 'ALL', effectiveYearBio)

        // Show loading indicator
        setIsLoading(true)
        const controller = beginFilterRequest()

        // Determine if we're requesting all years
        const isAllYears = Array.isArray(effectiveYearBio) ?
                          (effectiveYearBio.includes("ALL YEARS") || effectiveYearBio.length === 0) :
                          (effectiveYearBio === "ALL" || !effectiveYearBio);

        // Always show a loading message for Trawl Biology queries since they consistently take longer
        // Skip ALL snackbar messages for Bottom Longline Biology
        if (activeTab === 'TrawlBiology') {
            setSnackbarMessage("Loading Trawl Biology data... This query accesses a large database and may take some time.")
            setSnackbarOpen(true)
        }
        // For other biology tabs, only show message for all years queries if not Bottom Longline
        else if (isAllYears && !activeTab.includes('Longline')) {
            setSnackbarMessage("Loading data... This may take a moment for large datasets.")
            setSnackbarOpen(true)
        }

        // Determine the endpoint based on the active tab
        const endpoint = activeTab === 'TrawlBiology'
            ? '/trawl-biology'
            : (activeTab === 'PlanktonBiology' ? '/plankton-biology' : '/longline-biology')

        // Create season parameter
        const seasonParam = activeTab === 'TrawlBiology' || activeTab === 'PlanktonBiology'
            ? (selectedSeason || "ALL")
            : undefined;

        // Prepare year values from the effective year (which may be the
        // user's explicit picks, a span derived from the date range, or ALL).
        let yearValues = [];

        if (Array.isArray(effectiveYearBio)) {
            if (effectiveYearBio.includes("ALL YEARS") || effectiveYearBio.length === 0) {
                yearValues = ["ALL"];
            } else {
                // Convert string years to numbers
                yearValues = effectiveYearBio.map(yr =>
                    typeof yr === 'string' && !isNaN(yr) ? parseInt(yr, 10) : yr
                );
            }
        } else {
            yearValues = [effectiveYearBio === "ALL YEARS" ? "ALL" : (effectiveYearBio || "ALL")];
        }

        // Special handling for multiple years
        if (yearValues.length > 1) {
            // Track all promises
            const allFetchPromises = [];

            // Make separate request for each year
            yearValues.forEach(year => {
                const params = new URLSearchParams();
                appendSpeciesParams(params);
                params.append('year', year);
                appendDateRangeParams(params);

                if (seasonParam) {
                    params.append('season', seasonParam);
                }

                const requestUrl = `${API_URL}${endpoint}?${params.toString()}`;
                const fetchPromise = fetch(requestUrl, { signal: controller.signal })
                    .then(response => {
                        if (!response.ok) {
                            console.error(`API error for biology year ${year}:`, response.statusText);
                            return { year, error: response.statusText, data: [] };
                        }
                        return response.json().then(data => ({ year, data }));
                    })
                    .catch(error => {
                        if (isAbortError(error)) throw error
                        console.error(`Error fetching biology data for year ${year}:`, error);
                        return { year, error: error.message, data: [] };
                    });

                allFetchPromises.push(fetchPromise);
            });

            // Wait for all requests to complete
            Promise.all(allFetchPromises)
                .then(results => {
                    if (!isCurrentFilterRequest(controller)) return
                    setIsLoading(false);

                    // Process results and combine data
                    let combinedData = [];
                    let hasError = false;

                    results.forEach(result => {
                        if (result.error) {
                            hasError = true;
                        } else if (Array.isArray(result.data)) {
                            combinedData = [...combinedData, ...result.data];
                        }
                    });

                    if (hasError) {
                        // Only show error messages for Trawl, not for Bottom Longline
                        if (!activeTab.includes('Longline')) {
                            setSnackbarMessage("Some year data could not be fetched. Results may be incomplete.");
                            setSnackbarOpen(true);
                        }
                    }

                    if (combinedData.length === 0) {
                        // Only show no results message for Trawl, not for Bottom Longline
                        if (!activeTab.includes('Longline')) {
                            showNoResultsMessage();
                        }
                        setFilteredData([]);
                        return;
                    }

                    // Filter out stations with invalid coordinates
                    const validData = combinedData.filter(item => {
                        const lat = parseFloat(item.decslat);
                        const lon = parseFloat(item.decslon);
                        return !isNaN(lat) && !isNaN(lon) &&
                               lat !== 0 && lon !== 0 &&
                               lat >= -90 && lat <= 90 &&
                               lon >= -180 && lon <= 180;
                    });

                    // Check if any stations were filtered out due to invalid coordinates
                    if (validData.length < combinedData.length && activeTab !== 'TrawlStations' && !activeTab.includes('Longline')) {
                        setSnackbarMessage("Not all results contain valid coordinates, these are not displayed.");
                        setSnackbarOpen(true);
                    }

                    if (validData.length === 0) {
                        showNoResultsMessage();
                        setFilteredData([]);
                    } else {
                        setCurrentQueryContext(requestContext);
                        setFilteredDataWithRange(validData);

                        recordWebmapFilterEvent(validData.length);

                        // Collapse the filter after successful query on mobile only.
                        collapseFilterAfterApply();
                        setIsLoading(false);
                    }
                })
                .catch(error => {
                    if (isAbortError(error)) return
                    if (!isCurrentFilterRequest(controller)) return
                    console.error('Error processing multi-year biology requests:', error);
                    // Only show error for Trawl
                    if (!activeTab.includes('Longline')) {
                        showNoResultsMessage();
                    }
                    setIsLoading(false);
                });
        } else {
            // Single year request - standard approach
            const params = new URLSearchParams();
            appendSpeciesParams(params);
            params.append('year', yearValues[0]);
            appendDateRangeParams(params);

            if (seasonParam) {
                params.append('season', seasonParam);
            }

            // Make the API request
            fetch(`${API_URL}${endpoint}?${params.toString()}`, { signal: controller.signal })
                .then(response => {
                    if (!response.ok) {
                        console.error('Biology API error:', response.statusText);
                        throw new Error(`API returned ${response.status}: ${response.statusText}`);
                    }
                    return response.json();
                })
                .then(data => {
                    if (!isCurrentFilterRequest(controller)) return

                    if (data.length === 0) {
                        // Only show message for Trawl
                        if (!activeTab.includes('Longline')) {
                            showNoResultsMessage();
                        }
                        setIsLoading(false);
                        return;
                    }

                    // Filter out biology records with invalid coordinates
                    const validData = data.filter(item => {
                        const lat = parseFloat(item.decslat);
                        const lon = parseFloat(item.decslon);
                        return !isNaN(lat) && !isNaN(lon) &&
                               lat !== 0 && lon !== 0 &&
                               lat >= -90 && lat <= 90 &&
                               lon >= -180 && lon <= 180;
                    });

                    // Check if any stations were filtered out due to invalid coordinates
                    if (validData.length < data.length && activeTab !== 'TrawlStations' && !activeTab.includes('Longline')) {
                        setSnackbarMessage("Not all results contain valid coordinates, these are not displayed.");
                        setSnackbarOpen(true);
                    }

                    if (validData.length === 0) {
                        showNoResultsMessage();
                        setFilteredData([]);
                        setIsLoading(false);
                    } else {
                        setCurrentQueryContext(requestContext);
                        setFilteredDataWithRange(validData);

                        recordWebmapFilterEvent(validData.length);

                        // Collapse the filter after successful query on mobile only.
                        collapseFilterAfterApply();
                        setIsLoading(false);
                    }
                })
                .catch(error => {
                    if (isAbortError(error)) return
                    if (!isCurrentFilterRequest(controller)) return
                    console.error('Error fetching biology data:', error);
                    showNoResultsMessage();
                    setIsLoading(false);
                });
        }
    }

    // Modify renderBiologyFilterOverlay for a more modern look
    const renderBiologyFilterOverlay = () => {
        // Use the appropriate species list based on the active tab
        const speciesList = activeTab === 'PlanktonBiology'
            ? planktonSpecies
            : (activeTab === 'LonglineBiology' ? longlineSpecies : trawlSpecies)

        // Determine the title based on the active tab
        const getTitle = () => {
            let title = '';
            if (activeTab === 'TrawlBiology') {
                title = 'Trawl Biology';
                // Add season if it's selected
                if (selectedSeason && selectedSeason !== 'ALL') {
                    title += ` (${selectedSeason})`;
                }
                return title;
            }
            if (activeTab === 'LonglineBiology') return 'Longline Biology';
            if (activeTab === 'PlanktonBiology') {
                title = 'Plankton Biology';
                if (selectedSeason && selectedSeason !== 'ALL') {
                    title += ` (${selectedSeason})`;
                }
                return title;
            }
            return activeTab;
        };

        // If collapsed, show minimal bar
        if (isFilterCollapsed) {
            return (
                <Paper
                    elevation={0}
                    sx={{
                        position: 'fixed',
                        top: 116, // 100px header height + 16px spacing
                        left: 16,
                        zIndex: FILTER_OVERLAY_Z_INDEX,
                        background: 'rgba(45, 52, 54, 0.95)', // Solid warm charcoal background
                        padding: '12px 18px',
                        width: '260px',
                        backdropFilter: 'blur(8px)',
                        border: '1px solid rgba(32, 178, 170, 0.08)', // Subtle Light Sea Green border
                        borderRadius: '4px',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                            background: 'rgba(45, 52, 54, 1)', // Darker on hover
                        }
                    }}
                    onClick={() => setIsFilterCollapsed(false)}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography
                            sx={{
                                fontFamily: "'Nunito', 'Roboto', sans-serif",
                                fontSize: '15px',
                                fontWeight: 600,
                                color: '#20B2AA', // Light Sea Green text
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                            }}
                        >
                            {getTitle()}
                        </Typography>
                        <Box sx={{
                            color: '#20B2AA', // Light Sea Green chevron
                            display: 'flex',
                            alignItems: 'center',
                            transition: 'transform 0.2s ease-in-out'
                        }}>
                            <KeyboardArrowDown sx={{ fontSize: '20px' }} />
                        </Box>
                    </Box>
                </Paper>
            );
        }

        return (
            <Paper
                elevation={0}
                sx={{
                    position: 'fixed',
                    top: 116, // 100px header height + 16px spacing
                    left: 16,
                    zIndex: FILTER_OVERLAY_Z_INDEX,
                    background: 'rgba(45, 52, 54, 0.95)', // Solid warm charcoal background
                    padding: '12px 18px',
                    width: '260px',
                    backdropFilter: 'blur(8px)',
                    border: '1px solid rgba(32, 178, 170, 0.08)', // Subtle Light Sea Green border
                    borderRadius: '4px',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                        background: 'rgba(45, 52, 54, 1)', // Darker on hover
                    }
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, mt: 0 }}>
                    <Typography
                        sx={{
                            fontFamily: "'Nunito', 'Roboto', sans-serif",
                            fontSize: '15px',
                            fontWeight: 600,
                            color: '#20B2AA', // Light Sea Green text
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            display: 'inline-block'
                        }}
                    >
                        {getTitle()}
                    </Typography>
                    <Box
                        sx={{
                            color: '#20B2AA', // Light Sea Green chevron
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            transition: 'transform 0.2s ease-in-out',
                            '&:hover': {
                                transform: 'scale(1.1)'
                            }
                        }}
                        onClick={() => setIsFilterCollapsed(true)}
                    >
                        <KeyboardArrowUp sx={{ fontSize: '20px' }} />
                    </Box>
                </Box>

                <FormControl fullWidth size='small' sx={{ mb: 2 }}>
                    <Autocomplete
                        multiple
                        id="species-autocomplete"
                        options={speciesList}
                        value={selectedSpecies}
                        open={speciesDropdownOpen}
                        onOpen={() => setSpeciesDropdownOpen(true)}
                        onClose={() => setSpeciesDropdownOpen(false)}
                        popupIcon={
                            <KeyboardArrowDown
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSpeciesDropdownOpen(!speciesDropdownOpen);
                                }}
                                sx={{ cursor: 'pointer', fontSize: '20px' }}
                            />
                        }
                        disableCloseOnSelect={false}
                        blurOnSelect="mouse"
                        onChange={(event, newValue) => {
                            setSelectedSpecies(Array.isArray(newValue) ? newValue : []);
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder={selectedSpecies.length ? '' : (activeTab === 'PlanktonBiology' ? 'Taxon' : 'Species')}
                                size="small"
                                inputProps={{
                                    ...params.inputProps,
                                    'aria-label': 'Species'
                                }}
                                sx={{
                                    fontSize: '13px',
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.07)',
                                        '& fieldset': {
                                            borderColor: 'rgba(245, 229, 214, 0.3)', // Light cream
                                            borderWidth: '1px',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#FFF5E6', // Warm cream
                                            borderWidth: '1.5px',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#FFF5E6', // Warm cream
                                            borderWidth: '1.5px',
                                            boxShadow: '0 0 0 2px rgba(245, 229, 214, 0.2)', // Cream shadow
                                        },
                                    },
                                    '& .MuiInputLabel-root': {
                                        fontSize: '13px',
                                        color: '#FFF5E6', // Warm cream
                                    },
                                    '& .MuiInputBase-input': {
                                        color: '#FFF5E6', // Warm cream
                                    }
                                }}
                            />
                        )}
                        size="small"
                        sx={{
                            '& .MuiAutocomplete-inputRoot': {
                                minHeight: '36px',
                                alignItems: selectedSpecies.length ? 'flex-start' : 'center',
                                flexWrap: 'wrap',
                                overflow: 'hidden'
                            },
                            '& .MuiOutlinedInput-root': {
                                alignItems: selectedSpecies.length ? 'flex-start' : 'center',
                                overflow: 'hidden'
                            },
                            '& .MuiAutocomplete-input': {
                                fontSize: '13px',
                                padding: selectedSpecies.length ? '0px !important' : '0 0 0 8px !important'
                            },
                            '& .MuiAutocomplete-endAdornment': {
                                '& .MuiSvgIcon-root': {
                                    color: '#20B2AA'
                                }
                            },
                            '& .MuiAutocomplete-tag': {
                                maxWidth: '100%'
                            }
                        }}
                        renderTags={(value, getTagProps) => (
                            <Box sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 0.5,
                                alignItems: 'flex-start',
                                width: '100%',
                                maxWidth: '100%'
                            }}>
                                {value.map((option, index) => (
                                    <Chip
                                        key={`${option}-${index}`}
                                        label={option}
                                        {...getTagProps({ index })}
                                        size="small"
                                        sx={{
                                            backgroundColor: 'rgba(32, 178, 170, 0.1)',
                                            fontSize: '13px',
                                            height: '24px',
                                            maxWidth: '100%',
                                            '& .MuiChip-label': {
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                maxWidth: '200px'
                                            }
                                        }}
                                    />
                                ))}
                            </Box>
                        )}
                    />
                </FormControl>

                <FormControl fullWidth size='small' sx={{ mb: 2 }}>
                    <Autocomplete
                        multiple
                        id="year-multi-select-biology"
                        options={['--Select--', 'ALL YEARS', ...getYearsForTab().map(yr => String(yr))]}
                        value={Array.isArray(selectedYear) ? selectedYear : (selectedYear ? [selectedYear] : [])}
                        open={yearDropdownOpen}
                        onOpen={() => setYearDropdownOpen(true)}
                        onClose={() => setYearDropdownOpen(false)}
                        popupIcon={
                            <KeyboardArrowDown
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setYearDropdownOpen(!yearDropdownOpen);
                                }}
                                sx={{ cursor: 'pointer', fontSize: '20px' }}
                            />
                        }
                        disableCloseOnSelect={false}
                        blurOnSelect="mouse"
                        onChange={(e, newValue) => {
                            // If selecting "--Select--", ignore it
                            if (newValue.includes('--Select--')) {
                                const filteredValue = newValue.filter(val => val !== '--Select--');
                                setSelectedYear(filteredValue);
                                return;
                            }

                            // If selecting "ALL YEARS", clear other selections
                            if (newValue.includes('ALL YEARS') && newValue.length > 1) {
                                setSelectedYear(['ALL YEARS']);
                            } else if (newValue.includes('ALL YEARS') && newValue.length === 1) {
                                setSelectedYear(newValue);
                            } else {
                                // If selecting a year and "ALL YEARS" is already selected, remove "ALL YEARS"
                                const filteredValue = newValue.filter(val => val !== 'ALL YEARS');
                                setSelectedYear(filteredValue);
                            }
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder={selectedYear?.length ? '' : 'Year'}
                                size="small"
                                inputProps={{
                                    ...params.inputProps,
                                    'aria-label': 'Year'
                                }}
                                sx={{
                                    fontSize: '13px',
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.07)',
                                        '& fieldset': {
                                            borderColor: 'rgba(245, 229, 214, 0.3)', // Light cream
                                            borderWidth: '1px',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#FFF5E6', // Warm cream
                                            borderWidth: '1.5px',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#FFF5E6', // Warm cream
                                            borderWidth: '1.5px',
                                            boxShadow: '0 0 0 2px rgba(245, 229, 214, 0.2)', // Cream shadow
                                        },
                                    },
                                    '& .MuiInputLabel-root': {
                                        fontSize: '13px',
                                        color: '#FFF5E6', // Warm cream
                                    },
                                    '& .MuiInputBase-input': {
                                        color: '#FFF5E6', // Warm cream
                                    }
                                }}
                            />
                        )}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    label={option}
                                    {...getTagProps({ index })}
                                    size="small"
                                    sx={{
                                        backgroundColor: option === 'ALL YEARS' ? 'rgba(32, 178, 170, 0.2)' : 'rgba(32, 178, 170, 0.1)', // Light Sea Green background
                                        fontSize: '13px',
                                        fontWeight: option === 'ALL YEARS' ? 600 : 400,
                                        height: '24px',
                                        color: '#FFF5E6', // Warm cream
                                    }}
                                />
                            ))
                        }
                        renderOption={(props, option) => (
                            <li {...props} style={{
                                fontWeight: option === 'ALL YEARS' ? 600 : 400,
                                fontSize: '13px',
                                color: '#FFF5E6', // Warm cream
                            }}>
                                {option === '--Select--' ? renderMutedSelectPlaceholder('--Select--') : option}
                            </li>
                        )}
                        size='small'
                        sx={{
                            fontSize: '13px',
                            '& .MuiAutocomplete-inputRoot': {
                                minHeight: '36px'
                            },
                            '& .MuiAutocomplete-input': {
                                fontSize: '13px',
                                height: '1.4375em',
                                color: '#FFF5E6', // Warm cream
                            },
                            '& .MuiAutocomplete-endAdornment': {
                                '& .MuiSvgIcon-root': {
                                    color: '#20B2AA', // Light Sea Green
                                }
                            }
                        }}
                        ListboxProps={{
                            style: {
                                fontSize: '13px'
                            }
                        }}
                        PaperProps={{
                            sx: {
                                '&.MuiPaper-root': {
                                    backgroundColor: '#2D3436 !important', // Force charcoal background
                                },
                                backgroundColor: '#2D3436 !important', // Charcoal background - override theme
                                '& .MuiAutocomplete-option': {
                                    fontSize: '13px',
                                    minHeight: '32px',
                                    color: '#FFF5E6', // Warm cream
                                    '&:hover': {
                                        backgroundColor: 'rgba(32, 178, 170, 0.15)', // Light Sea Green
                                    },
                                    '&.Mui-selected': {
                                        backgroundColor: 'rgba(32, 178, 170, 0.25)', // Light Sea Green
                                        '&:hover': {
                                            backgroundColor: 'rgba(32, 178, 170, 0.35)', // Light Sea Green
                                        }
                                    }
                                }
                            }
                        }}
                    />
                </FormControl>

                {/* Season dropdown for biology tabs with seasonal filters */}
                {(activeTab === 'TrawlBiology' || activeTab === 'PlanktonBiology') && (
                    <FormControl fullWidth size='small' sx={{ mb: 2 }}>
                        <Select
                            id="biology-season-select"
                            value={selectedSeason}
                            displayEmpty
                            renderValue={(value) => {
                                if (!value) return renderMutedSelectPlaceholder('Season')
                                if (value === 'ALL') return 'ALL SEASONS'
                                return value
                            }}
                            onChange={(e) => setSelectedSeason(e.target.value)}
                            size='small'
                            IconComponent={KeyboardArrowDown}
                            inputProps={{
                                id: 'biology-season-input',
                                name: 'biology-season',
                                'aria-label': 'Season'
                            }}
                            sx={{
                                fontSize: '13px',
                                height: '36px',
                                color: '#FFF5E6', // Warm cream
                                backgroundColor: 'rgba(255, 255, 255, 0.07)',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(245, 229, 214, 0.3)', // Light cream
                                    borderWidth: '1px',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#FFF5E6', // Warm cream
                                    borderWidth: '1.5px',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#FFF5E6', // Warm cream
                                    borderWidth: '1.5px',
                                    boxShadow: '0 0 0 2px rgba(245, 229, 214, 0.2)', // Cream shadow
                                },
                                '& .MuiSelect-select': {
                                    paddingTop: '6px',
                                    paddingBottom: '6px'
                                },
                                '& .MuiSvgIcon-root': {
                                    color: '#20B2AA', // Light Sea Green
                                }
                            }}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        '&.MuiPaper-root.MuiMenu-paper': {
                                            backgroundColor: '#2D3436 !important', // Force charcoal background
                                        },
                                        backgroundColor: '#2D3436 !important', // Charcoal background - override theme
                                        '& .MuiMenuItem-root': {
                                            fontSize: '13px',
                                            minHeight: '32px',
                                            color: '#FFF5E6', // Warm cream
                                            '&:hover': {
                                                backgroundColor: 'rgba(32, 178, 170, 0.15)', // Light Sea Green
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: 'rgba(32, 178, 170, 0.25)', // Light Sea Green
                                                '&:hover': {
                                                    backgroundColor: 'rgba(32, 178, 170, 0.35)', // Light Sea Green
                                                }
                                            }
                                        }
                                    }
                                }
                            }}
                        >
                            <MenuItem value="" sx={{ fontSize: '13px' }}>
                                {renderMutedSelectPlaceholder('--Select--')}
                            </MenuItem>
                            <MenuItem value="ALL" sx={{ fontSize: '13px', fontWeight: 600 }}>
                                ALL SEASONS
                            </MenuItem>
                            {activeTab === 'PlanktonBiology' && (
                                <MenuItem value="Spring" sx={{ fontSize: '13px' }}>Spring</MenuItem>
                            )}
                            {activeTab !== 'PlanktonBiology' && (
                                <MenuItem value="Summer" sx={{ fontSize: '13px' }}>Summer</MenuItem>
                            )}
                            <MenuItem value="Fall" sx={{ fontSize: '13px' }}>Fall</MenuItem>
                        </Select>
                    </FormControl>
                )}

                <DateRangeFields
                    dateRangeStart={dateRangeStart}
                    setDateRangeStart={setDateRangeStart}
                    dateRangeEnd={dateRangeEnd}
                    setDateRangeEnd={setDateRangeEnd}
                />

                <Button
                    fullWidth
                    variant="contained"
                    onClick={handleBiologyFilter}
                    sx={{
                        background: '#20B2AA', // Light Sea Green button
                        color: '#FFF5E6', // Warm cream text
                        textTransform: 'none',
                        fontWeight: 600,
                        padding: '8px 0',
                        borderRadius: '4px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            background: '#1A9B99', // Darker Light Sea Green on hover
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            transform: 'translateY(-1px)'
                        }
                    }}
                >
                    Apply Filters
                </Button>

                {/* Show trawl shapefile toggle for trawl biology tab only */}
                {activeTab === 'TrawlBiology' && (
                    <>
                        <Box sx={{ mt: 2 }} />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showTrawlShapefile}
                                    onChange={(e) => setShowTrawlShapefile(e.target.checked)}
                                    size="small"
                                    sx={commonSwitchStyles}
                                />
                            }
                            label={
                                <Typography sx={{ fontSize: '13px', color: '#f2f4f6' }}>
                                    Show Current Trawl Zones
                                </Typography>
                            }
                            sx={{
                                margin: 0,
                                width: '100%',
                                justifyContent: 'space-between'
                            }}
                            labelPlacement="start"
                        />

                        {/* Add Historic Trawl shapefile toggle */}
                        <Box sx={{ mt: 1 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={showHistoricTrawlShapefile}
                                        onChange={(e) => setShowHistoricTrawlShapefile(e.target.checked)}
                                        size="small"
                                        sx={commonSwitchStyles}
                                    />
                                }
                                label={
                                    <Typography sx={{ fontSize: '13px', color: '#f2f4f6' }}>
                                        Show Historic Trawl Zones
                                    </Typography>
                                }
                                sx={{
                                    margin: 0,
                                    width: '100%',
                                    justifyContent: 'space-between'
                                }}
                                labelPlacement="start"
                            />
                        </Box>
                    </>
                )}

                {/* Show Longline shapefile toggles for the longline biology tab only */}
                {activeTab === 'LonglineBiology' && (
                    <>
                        <Box sx={{ mt: 2 }} />
                        {/* 2008 Longline Zones toggle */}
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showLongline2008Shapefile}
                                    onChange={(e) => setShowLongline2008Shapefile(e.target.checked)}
                                    size="small"
                                    sx={commonSwitchStyles}
                                />
                            }
                            label={
                                <Typography sx={{ fontSize: '13px', color: '#f2f4f6' }}>
                                    Show 2008 Longline Zones
                                </Typography>
                            }
                            sx={{
                                margin: 0,
                                width: '100%',
                                justifyContent: 'space-between'
                            }}
                            labelPlacement="start"
                        />

                        {/* 2015 Longline Zones toggle */}
                        <Box sx={{ mt: 1 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={showLongline2015Shapefile}
                                        onChange={(e) => setShowLongline2015Shapefile(e.target.checked)}
                                        size="small"
                                        sx={commonSwitchStyles}
                                    />
                                }
                                label={
                                    <Typography sx={{ fontSize: '13px', color: '#f2f4f6' }}>
                                        Show 2015 Longline Zones
                                    </Typography>
                                }
                                sx={{
                                    margin: 0,
                                    width: '100%',
                                    justifyContent: 'space-between'
                                }}
                                labelPlacement="start"
                            />
                        </Box>

                        {/* 2024 Longline Zones toggle */}
                        <Box sx={{ mt: 1 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={showLongline2024Shapefile}
                                        onChange={(e) => setShowLongline2024Shapefile(e.target.checked)}
                                        size="small"
                                        sx={commonSwitchStyles}
                                    />
                                }
                                label={
                                    <Typography sx={{ fontSize: '13px', color: '#f2f4f6' }}>
                                        Show 2024 Longline Zones
                                    </Typography>
                                }
                                sx={{
                                    margin: 0,
                                    width: '100%',
                                    justifyContent: 'space-between'
                                }}
                                labelPlacement="start"
                            />
                        </Box>
                    </>
                )}


            </Paper>
        )
    }

    // Check if the current tab is a station tab
    const isStationTab = activeTab === 'TrawlStations' || activeTab === 'LonglineStations' || activeTab === 'PlanktonStations'

    // Check if the current tab is a biology tab
    const isBiologyTab = activeTab === 'TrawlBiology' || activeTab === 'LonglineBiology' || activeTab === 'PlanktonBiology'
    const supportsBiologyHeatmap = activeTab === 'TrawlBiology' || activeTab === 'LonglineBiology'

    const buildPointMarkerModel = useCallback((item, index) => {
        const lat = parseFloat(item.decslat)
        const lon = parseFloat(item.decslon)

        if (isNaN(lat) || isNaN(lon) ||
            lat === 0 || lon === 0 ||
            lat < -90 || lat > 90 ||
            lon < -180 || lon > 180) {
            return null
        }

        const displayStationId = (activeTab === 'TrawlBiology' || activeTab === 'TrawlStations')
            ? (item.s_sta_no || "Unknown")
            : (item.s_sta_no || item.stationid || item.v_c_p || "Unknown")

        return {
            key: `${activeTab}-${item.stationid || item.sid || item.s_sta_no || index}-${index}`,
            item,
            lat,
            lon,
            displayStationId
        }
    }, [activeTab])

    const stationMarkerModels = useMemo(() => {
        if (!isStationTab || filteredData.length === 0) return []
        return filteredData
            .map(buildPointMarkerModel)
            .filter(Boolean)
    }, [filteredData, isStationTab, buildPointMarkerModel])

    const biologyLegendItems = useMemo(() => {
        if (!isBiologyTab || filteredData.length === 0) return []
        return getSpeciesLegend(filteredData, selectedSpecies)
    }, [filteredData, selectedSpecies, isBiologyTab])

    const biologyMarkerModels = useMemo(() => {
        if (!isBiologyTab || filteredData.length === 0 || mapDisplayMode !== 'markers') return []
        return filteredData
            .map((item, index) => {
                const marker = buildPointMarkerModel(item, index)
                if (!marker) return null
                return {
                    ...marker,
                    fillColor: getSpeciesColor(item, selectedSpecies)
                }
            })
            .filter(Boolean)
    }, [filteredData, isBiologyTab, mapDisplayMode, buildPointMarkerModel, selectedSpecies])

    // Check if this is a trawl tab
    const isTrawlTab = activeTab.includes('Trawl')
    const activeBasemap = BASEMAP_OPTIONS.find(option => option.id === activeBasemapId) || BASEMAP_OPTIONS[0]
    const heatmapZeroCellBorderColor = activeBasemapId === 'imagery' || activeBasemapId === 'minimal'
        ? HEATMAP_IMAGERY_ZERO_CELL_BORDER
        : HEATMAP_ZERO_CELL_BORDER

    useEffect(() => {
        if (!basemapMenuOpen) return undefined
        if (isMobileBasemapMenu) return undefined

        const handleOutsidePointer = (event) => {
            if (basemapMenuRef.current?.contains(event.target)) return
            setBasemapMenuOpen(false)
        }

        document.addEventListener('mousedown', handleOutsidePointer, true)
        document.addEventListener('touchstart', handleOutsidePointer, true)
        return () => {
            document.removeEventListener('mousedown', handleOutsidePointer, true)
            document.removeEventListener('touchstart', handleOutsidePointer, true)
        }
    }, [basemapMenuOpen, isMobileBasemapMenu])

    const currentQuerySpeciesCount = Array.isArray(currentQueryContext?.filters?.species)
        ? currentQueryContext.filters.species.filter(species => typeof species === 'string' && species.trim()).length
        : 0
    const isHeatmapBlockedBySpecies = supportsBiologyHeatmap && currentQuerySpeciesCount > 1

    const buildExportPayload = useCallback((bounds = null) => {
        if (!currentQueryContext) return null
        return {
            ...currentQueryContext,
            bounds: bounds ? boundsToPayload(bounds) : undefined
        }
    }, [currentQueryContext])

    const fetchExportSummary = useCallback(async (bounds = null) => {
        const payload = buildExportPayload(bounds)
        if (!payload) return null
        const response = await fetch(`${API_URL}/export-records`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        })
        if (!response.ok) {
            throw new Error(`Export request failed with ${response.status}`)
        }
        const data = await response.json()
        return { ...data, payload }
    }, [buildExportPayload])

    const handleAreaSelectionComplete = useCallback(async (bounds) => {
        if (!currentQueryContext || !bounds) return
        setAreaSelectionEnabled(false)
        setAreaSelectionLoading(true)
        setAreaSelectionError('')
        setAreaSelectionData(null)
        setAreaSelectionDialogOpen(true)
        try {
            const data = await fetchExportSummary(bounds)
            setAreaSelectionData(data)
        } catch (error) {
            console.error('Area selection export preview failed:', error)
            setAreaSelectionError('Could not load records for the selected area.')
        } finally {
            setAreaSelectionLoading(false)
        }
    }, [currentQueryContext, fetchExportSummary])

    const handleAreaSelectionExport = async () => {
        if (!areaSelectionData?.payload) return
        setAreaSelectionLoading(true)
        setAreaSelectionError('')
        try {
            const response = await fetch(`${API_URL}/export-records/download`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(areaSelectionData.payload)
            })
            if (!response.ok) {
                throw new Error(`Export download failed with ${response.status}`)
            }
            const blob = await response.blob()
            downloadBlob(blob, getZipFilename(activeTab, 'selection-records'))
        } catch (error) {
            console.error('Area selection export download failed:', error)
            setAreaSelectionError('Could not download records for the selected area.')
        } finally {
            setAreaSelectionLoading(false)
        }
    }

    const handleClearAreaSelection = () => {
        setAreaSelectionDialogOpen(false)
        setAreaSelectionData(null)
        setAreaSelectionError('')
        setAreaSelectionPreview(null)
        setAreaSelectionEnabled(false)
    }

    const handleDisplayModeChange = (nextMode) => {
        if (nextMode === 'heatmap' && !supportsBiologyHeatmap) {
            setMapDisplayMode('markers')
            setBiologyHeatmapGrid(null)
            setBiologyHeatmapError('')
            return
        }
        if (nextMode === 'heatmap' && isHeatmapBlockedBySpecies) {
            setMapDisplayMode('markers')
            setBiologyHeatmapGrid(null)
            setBiologyHeatmapError('')
            return
        }
        setMapDisplayMode(nextMode)
    }

    useEffect(() => {
        setBiologyHeatmapGrid(null)
        setBiologyHeatmapError('')
    }, [currentQueryContext])

    useEffect(() => {
        if (!isHeatmapBlockedBySpecies || mapDisplayMode !== 'heatmap') return
        setMapDisplayMode('markers')
        setBiologyHeatmapGrid(null)
        setBiologyHeatmapError('')
        setBiologyHeatmapLoading(false)
    }, [isHeatmapBlockedBySpecies, mapDisplayMode])

    useEffect(() => {
        if (mapDisplayMode !== 'heatmap' || !currentQueryContext || !supportsBiologyHeatmap || isHeatmapBlockedBySpecies) {
            setBiologyHeatmapError('')
            return
        }

        const controller = new AbortController()
        const mode = 'biology'
        const requestKey = JSON.stringify({
            context: currentQueryContext,
            mode,
            metric: heatmapMetric
        })

        if (biologyHeatmapGrid?.requestKey === requestKey) return

        setBiologyHeatmapLoading(true)
        setBiologyHeatmapError('')
        const endpoint = activeTab === 'LonglineBiology'
            ? '/longline-hook-effort/grid'
            : '/trawl-swept-area/grid'

        fetch(`${API_URL}${endpoint}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                ...currentQueryContext,
                mode,
                metric: heatmapMetric
            }),
            signal: controller.signal
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`Biology heat map request failed with ${response.status}`)
                }
                return response.json()
            })
            .then(data => {
                if (data.gridSystemId !== HEATMAP_GRID_SYSTEM_ID) {
                    throw new Error(`Unexpected grid system: ${data.gridSystemId || 'missing'}`)
                }
                setBiologyHeatmapGrid({
                    requestKey,
                    cells: Array.isArray(data.cells) ? data.cells : [],
                    metadata: data.metadata || {}
                })
            })
            .catch(error => {
                if (error.name === 'AbortError') return
                console.error('Biology heat map request failed:', error)
                setBiologyHeatmapError('Heat map unavailable')
                setBiologyHeatmapGrid(null)
            })
            .finally(() => {
                if (!controller.signal.aborted) {
                    setBiologyHeatmapLoading(false)
                }
            })

        return () => controller.abort()
    }, [mapDisplayMode, currentQueryContext, activeTab, heatmapMetric, biologyHeatmapGrid, supportsBiologyHeatmap, isHeatmapBlockedBySpecies])

    const biologyHeatmapScale = useMemo(() => {
        if (!biologyHeatmapGrid?.metadata) return null
        return buildHeatmapColorScale(biologyHeatmapGrid.cells || [], biologyHeatmapGrid.metadata)
    }, [biologyHeatmapGrid])

    // Reset shapefile display when changing away from trawl tabs
    useEffect(() => {
        if (!isTrawlTab) {
            resetTrawlZoneLayers()
        }
    }, [isTrawlTab, resetTrawlZoneLayers])

    // Component to handle map bounds fitting
    function MapBoundsControl({ markers }) {
        const map = useMap();

        useEffect(() => {
            // Only fit bounds when the shouldFitBoundsRef is true
            // This prevents zooming when clicking on points
            if (markers && markers.length > 0 && shouldFitBoundsRef.current) {
                const validCoords = markers.map(marker => [marker.lat, marker.lon]);

                if (validCoords.length > 0) {
                    // Create bounds object
                    const bounds = L.latLngBounds(validCoords.map(coord => L.latLng(coord[0], coord[1])));

                    if (bounds.isValid()) {
                        // Fit map to bounds with padding
                        map.fitBounds(bounds, {
                            padding: [50, 50],
                            maxZoom: 10,
                            animate: true
                        });

                        // Reset the flag after fitting bounds
                        shouldFitBoundsRef.current = false;
                    }
                }
            }
        }, [map, markers]);

        return null;
    }

    // Create a map reference component
    function MapReference() {
        const map = useMap();

        // Store the map instance in the ref when the component mounts
        useEffect(() => {
            mapRef.current = map;
            return () => {
                mapRef.current = null;
            };
        }, [map]);

        return null;
    }

    function BiologyHeatmapLegend({ metadata, scale, zeroCellBorderColor = HEATMAP_ZERO_CELL_BORDER }) {
        if (!metadata) return null
        const scaleClasses = Array.isArray(scale?.classes) ? scale.classes : []
        const metricInfo = getHeatmapMetricInfo(metadata)
        return (
            <Box sx={{
                mt: 0.5,
                pt: 0,
                [COMPACT_BIOLOGY_OVERLAY_QUERY]: {
                    mt: 0.35,
                    pt: 0
                }
            }}>
                <Typography sx={{
                    fontSize: '14px',
                    color: '#FFF5E6',
                    mb: 1,
                    fontWeight: 'bold',
                    [COMPACT_BIOLOGY_OVERLAY_QUERY]: {
                        fontSize: '13px',
                        mb: 0.6
                    }
                }}>
                    {metricInfo.label}
                </Typography>
                <Box sx={{ mt: 0.85 }}>
                    <Typography sx={{
                        fontSize: '11px',
                        color: '#b8d8d6',
                        lineHeight: 1.3,
                        fontWeight: 700,
                        mb: 0.75,
                        [COMPACT_BIOLOGY_OVERLAY_QUERY]: {
                            fontSize: '10.5px',
                            lineHeight: 1.15,
                            mb: 0.5
                        }
                    }}>
                        {metricInfo.description}
                    </Typography>
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: scaleClasses.length > 6 ? '1fr 1fr' : '1fr',
                        columnGap: 0.75,
                        rowGap: 0.25,
                        [COMPACT_BIOLOGY_OVERLAY_QUERY]: {
                            rowGap: 0.1
                        }
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                            <Box sx={{
                                width: 14,
                                height: 9,
                                backgroundColor: 'transparent',
                                border: `1.5px solid ${zeroCellBorderColor}`,
                                mr: 0.5,
                                flexShrink: 0,
                                [COMPACT_BIOLOGY_OVERLAY_QUERY]: {
                                    width: 12,
                                    height: 8
                                }
                            }} />
                            <Typography sx={{
                                fontSize: '10px',
                                color: '#f2f4f6',
                                lineHeight: 1.2,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                [COMPACT_BIOLOGY_OVERLAY_QUERY]: {
                                    fontSize: '9.5px',
                                    lineHeight: 1.1
                                }
                            }}>
                                0
                            </Typography>
                        </Box>
                        {scaleClasses.map((item) => (
                            <Box key={`${item.label}-${item.color}`} sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                                <Box sx={{
                                    width: 14,
                                    height: 9,
                                    backgroundColor: item.color,
                                    border: '1px solid rgba(255, 255, 255, 0.45)',
                                    mr: 0.5,
                                    flexShrink: 0,
                                    [COMPACT_BIOLOGY_OVERLAY_QUERY]: {
                                        width: 12,
                                        height: 8
                                    }
                                }} />
                                <Typography sx={{
                                    fontSize: '10px',
                                    color: '#f2f4f6',
                                    lineHeight: 1.2,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    [COMPACT_BIOLOGY_OVERLAY_QUERY]: {
                                        fontSize: '9.5px',
                                        lineHeight: 1.1
                                    }
                                }}>
                                    {item.label}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
                {scaleClasses.length === 0 && (
                    <Typography sx={{
                        fontSize: '12px',
                        color: '#f2f4f6',
                        lineHeight: 1.35,
                        fontWeight: 500,
                        mt: 0.75,
                        [COMPACT_BIOLOGY_OVERLAY_QUERY]: {
                            fontSize: '11.5px',
                            lineHeight: 1.25,
                            mt: 0.55
                        }}}>
                        No positive values in current scale
                    </Typography>
                )}
            </Box>
        )
    }

    function BiologyHeatmapInfoOverlay({
        metadata,
        loading,
        error,
        selectionEnabled,
        collapsed,
        onToggle,
        onOverlayPointerEnter,
        onOverlayPointerLeave
    }) {
        const panelRef = useRef(null)
        const valid = Number(metadata?.valid_tow_count || metadata?.valid_set_count || 0)
        const metricLabel = HEATMAP_METRIC_LABELS[metadata?.metric] || 'Heat map'
        const isHookEffort = metadata?.effort_method === 'hooks_deployed'
        const sampleLabel = isHookEffort ? 'sets' : 'tows'
        const stateLine = metadata?.metric === 'effort'
            ? 'Filled cells show eligible sampling effort; no cell means no eligible effort.'
            : 'Colorless cells had effort within the cell, but did not encounter the species within that cell.'
        const selectionGuideLine = selectionEnabled
            ? 'Select area is active: drag a rectangle on the map to preview and export the underlying point records.'
            : 'When viewing heat maps, select area allows users to select all stations within the selected 10’ x 10’ cells and export the station/catch records.'
        const detailRows = [
            ['Metric', metricLabel],
            ['Unit', metadata?.unit || (isHookEffort ? 'individuals/100 hooks deployed' : 'individuals/hectare swept')],
            ['Cells', '10-minute longitude by 10-minute latitude'],
            ['Effort', `${valid.toLocaleString()} ${sampleLabel}`]
        ]
        const guideLines = [
            'The heat map summarizes the same filtered biology result set shown as points.',
            'Users can change species, source, year, season, or date filters and apply them to rebuild both the points and the heat map.',
            selectionGuideLine
        ]

        useEffect(() => {
            const panel = panelRef.current
            if (!panel) return undefined

            const handlePanelWheel = (event) => {
                event.preventDefault()
                event.stopPropagation()
                if (typeof event.stopImmediatePropagation === 'function') {
                    event.stopImmediatePropagation()
                }

                const hasScrollableContent = panel.scrollHeight > panel.clientHeight
                if (!hasScrollableContent || event.deltaY === 0) return

                let scrollDelta = event.deltaY
                if (event.deltaMode === 1) {
                    scrollDelta *= 16
                } else if (event.deltaMode === 2) {
                    scrollDelta *= panel.clientHeight
                }
                panel.scrollTop += scrollDelta
            }

            if (collapsed) return undefined

            panel.addEventListener('wheel', handlePanelWheel, { capture: true, passive: false })
            return () => {
                panel.removeEventListener('wheel', handlePanelWheel, true)
                onOverlayPointerLeave?.()
            }
        }, [collapsed, onOverlayPointerLeave])

        return (
            <Box ref={panelRef} onPointerEnter={onOverlayPointerEnter} onPointerMove={onOverlayPointerEnter} onPointerLeave={onOverlayPointerLeave} sx={{
                width: '100%',
                minHeight: 0,
                maxHeight: collapsed ? 'none' : { xs: 'min(300px, 44vh)', md: 'min(420px, 48vh)' },
                overflowY: collapsed ? 'visible' : 'auto',
                scrollbarGutter: 'stable',
                overscrollBehavior: 'contain',
                touchAction: 'pan-y',
                backgroundColor: 'rgba(45, 52, 54, 0.95)',
                backdropFilter: 'blur(8px)',
                border: '1px solid rgba(32, 178, 170, 0.12)',
                borderRadius: '4px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.18)',
                p: collapsed ? 1.25 : 1.5,
                [COMPACT_BIOLOGY_OVERLAY_QUERY]: {
                    maxHeight: collapsed ? 'none' : 'min(340px, 44vh)',
                    p: collapsed ? 0.85 : 1
                },
                '&::-webkit-scrollbar': {
                    width: '4px',
                },
                '&::-webkit-scrollbar-track': {
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '2px',
                },
                '&::-webkit-scrollbar-thumb': {
                    background: 'rgba(255, 255, 255, 0.3)',
                    borderRadius: '2px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                    background: 'rgba(255, 255, 255, 0.5)',
                },
            }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    borderBottom: collapsed ? 'none' : '1px solid rgba(255, 245, 230, 0.2)',
                    pb: collapsed ? 0 : 1,
                    mb: collapsed ? 0 : 1.25,
                    [COMPACT_BIOLOGY_OVERLAY_QUERY]: {
                        pb: collapsed ? 0 : 0.65,
                        mb: collapsed ? 0 : 0.85
                    }
                }}>
                    <Typography sx={{
                        fontSize: '14px',
                        color: '#FFF5E6',
                        fontWeight: 700,
                        flex: 1,
                        minWidth: 0,
                        [COMPACT_BIOLOGY_OVERLAY_QUERY]: {
                            fontSize: '13px'
                        }
                    }}>
                        Details
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={onToggle}
                        aria-label={collapsed ? 'Expand details panel' : 'Collapse details panel'}
                        sx={{
                            color: '#FFF5E6',
                            p: 0.25,
                            ml: 1,
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)'
                            }
                        }}
                    >
                        {collapsed ? <KeyboardArrowDown sx={{ fontSize: '20px' }} /> : <KeyboardArrowUp sx={{ fontSize: '20px' }} />}
                    </IconButton>
                </Box>

                {!collapsed && (
                    <>
                {loading && (
                    <Typography sx={{ fontSize: '12px', color: '#f2f4f6', lineHeight: 1.35, fontWeight: 500 }}>
                        Loading details...
                    </Typography>
                )}

                {!loading && error && (
                    <Typography sx={{ fontSize: '12px', color: '#f2a6a6', lineHeight: 1.35, fontWeight: 700 }}>
                        {error}
                    </Typography>
                )}

                {!loading && !error && metadata && (
                    <Box sx={{
                        display: 'grid',
                        gap: 0.65,
                        [COMPACT_BIOLOGY_OVERLAY_QUERY]: {
                            gap: 0.45
                        }
                    }}>
                        {detailRows.map(([label, value]) => (
                            <Box key={label} sx={{
                                display: 'grid',
                                gridTemplateColumns: '88px minmax(0, 1fr)',
                                gap: 0.75,
                                alignItems: 'baseline',
                                [COMPACT_BIOLOGY_OVERLAY_QUERY]: {
                                    gridTemplateColumns: '78px minmax(0, 1fr)',
                                    gap: 0.5
                                }
                            }}>
                                <Typography sx={{
                                    fontSize: '11px',
                                    color: '#b8d8d6',
                                    fontWeight: 700,
                                    lineHeight: 1.3,
                                    [COMPACT_BIOLOGY_OVERLAY_QUERY]: {
                                        fontSize: '10.5px',
                                        lineHeight: 1.2
                                    }
                                }}>
                                    {label}
                                </Typography>
                                <Typography sx={{
                                    fontSize: '12px',
                                    color: '#f2f4f6',
                                    fontWeight: 500,
                                    lineHeight: 1.35,
                                    wordBreak: 'break-word',
                                    [COMPACT_BIOLOGY_OVERLAY_QUERY]: {
                                        fontSize: '11.5px',
                                        lineHeight: 1.25
                                    }
                                }}>
                                    {value}
                                </Typography>
                            </Box>
                        ))}
                        <Typography sx={{
                            fontSize: '12px',
                            color: '#f2f4f6',
                            fontWeight: 500,
                            lineHeight: 1.35,
                            mt: 0.25,
                            [COMPACT_BIOLOGY_OVERLAY_QUERY]: {
                                fontSize: '11.5px',
                                lineHeight: 1.25
                            }
                        }}>
                            {stateLine}
                        </Typography>
                    </Box>
                )}

                <Box sx={{
                    mt: 1.5,
                    pt: 1.25,
                    borderTop: '1px solid rgba(255, 245, 230, 0.2)',
                    [COMPACT_BIOLOGY_OVERLAY_QUERY]: {
                        mt: 1.15,
                        pt: 0.9
                    }
                }}>
                    <Typography sx={{
                        fontSize: '12px',
                        color: '#FFF5E6',
                        lineHeight: 1.35,
                        fontWeight: 700,
                        mb: 0.65,
                        [COMPACT_BIOLOGY_OVERLAY_QUERY]: {
                            fontSize: '11.5px',
                            lineHeight: 1.25,
                            mb: 0.45
                        }
                    }}>
                        Using heat maps
                    </Typography>
                    <Box sx={{
                        display: 'grid',
                        gap: 0.65,
                        [COMPACT_BIOLOGY_OVERLAY_QUERY]: {
                            gap: 0.45
                        }
                    }}>
                        {guideLines.map((line) => (
                            <Typography key={line} sx={{
                                fontSize: '12px',
                                color: '#f2f4f6',
                                lineHeight: 1.35,
                                fontWeight: 500,
                                [COMPACT_BIOLOGY_OVERLAY_QUERY]: {
                                    fontSize: '11.5px',
                                    lineHeight: 1.25
                                }
                            }}>
                                {line}
                            </Typography>
                        ))}
                    </Box>
                </Box>
                    </>
                )}
            </Box>
        )
    }

    function BiologyHeatmapGridLayer({ cells, metadata, scale, areaSelectionEnabled, basemapId }) {
        const metric = metadata?.metric || 'raw'
        const isEffortMetric = metric === 'effort'
        const metricKey = scale?.metricKey || getHeatmapMetricKey(metadata)
        const zeroCellBorderColor = basemapId === 'imagery' || basemapId === 'minimal'
            ? HEATMAP_IMAGERY_ZERO_CELL_BORDER
            : HEATMAP_ZERO_CELL_BORDER
        if (!Array.isArray(cells) || cells.length === 0) return null

        return (
            <>
                {cells.map((cell, index) => {
                    const rawMetricValue = cell[metricKey]
                    const metricValue = Number(rawMetricValue || 0)
                    const hasCatch = Number(cell.catch_count || 0) > 0
                    const bounds = [
                        [Number(cell.south), Number(cell.west)],
                        [Number(cell.north), Number(cell.east)]
                    ]
                    const cellKey = `heat-cell-fill-${index}-${cell.south}-${cell.west}`
                    return (
                        <HeatmapCellRectangle
                            key={cellKey}
                            cellKey={cellKey}
                            cell={cell}
                            bounds={bounds}
                            pathOptions={{
                                stroke: false,
                                fillColor: (hasCatch || isEffortMetric) ? (scale?.getColor(metricValue) || '#ffffff') : '#ffffff',
                                fillOpacity: (hasCatch || (isEffortMetric && metricValue > 0)) ? 1 : 0
                            }}
                            interactive={!areaSelectionEnabled}
                            metadata={metadata}
                            metricKey={metricKey}
                            metricValue={rawMetricValue}
                        />
                    )
                })}
                {cells.map((cell, index) => {
                    const hasCatch = Number(cell.catch_count || 0) > 0
                    const bounds = [
                        [Number(cell.south), Number(cell.west)],
                        [Number(cell.north), Number(cell.east)]
                    ]
                    return (
                        <Rectangle
                            key={`heat-cell-border-${index}-${cell.south}-${cell.west}`}
                            bounds={bounds}
                            pathOptions={{
                                color: hasCatch || isEffortMetric ? HEATMAP_ZERO_CELL_BORDER : zeroCellBorderColor,
                                weight: hasCatch || isEffortMetric ? 0.85 : 1.15,
                                opacity: 1,
                                dashArray: null,
                                fill: false
                            }}
                            interactive={false}
                        />
                    )
                })}
            </>
        )
    }

    function AreaSelectionControl({ enabled, onPreviewChange, onComplete }) {
        const map = useMap()
        const startRef = useRef(null)
        const moveRef = useRef(null)

        useMapEvents({
            mousedown: (event) => {
                if (!enabled || event.originalEvent.button !== 0) return
                L.DomEvent.stop(event.originalEvent)
                startRef.current = event.latlng
                onPreviewChange(L.latLngBounds(event.latlng, event.latlng))
                map.dragging.disable()

                const handleMove = (moveEvent) => {
                    if (!startRef.current) return
                    const bounds = L.latLngBounds(startRef.current, moveEvent.latlng)
                    onPreviewChange(bounds)
                }

                const handleUp = (upEvent) => {
                    map.off('mousemove', handleMove)
                    map.dragging.enable()
                    const start = startRef.current
                    startRef.current = null
                    moveRef.current = null
                    onPreviewChange(null)
                    if (!start) return
                    const bounds = L.latLngBounds(start, upEvent.latlng)
                    const north = bounds.getNorth()
                    const south = bounds.getSouth()
                    const east = bounds.getEast()
                    const west = bounds.getWest()
                    if (Math.abs(north - south) < 0.005 || Math.abs(east - west) < 0.005) {
                        return
                    }
                    onComplete(bounds)
                }

                moveRef.current = handleMove
                map.on('mousemove', handleMove)
                map.once('mouseup', handleUp)
            }
        })

        useEffect(() => {
            const container = map.getContainer()
            container.classList.toggle('area-selecting', enabled)
            container.style.cursor = enabled ? 'crosshair' : ''
            return () => {
                container.classList.remove('area-selecting')
                container.style.cursor = ''
            }
        }, [enabled, map])

        useEffect(() => {
            if (!enabled && moveRef.current) {
                map.off('mousemove', moveRef.current)
                map.dragging.enable()
                moveRef.current = null
                startRef.current = null
                onPreviewChange(null)
            }
        }, [enabled, map, onPreviewChange])

        return null
    }

    const showBiologyLegend = isBiologyTab && filteredData.length > 0 && isMapVisible
    const showHeatmapInfoOverlay = supportsBiologyHeatmap && isMapVisible && mapDisplayMode === 'heatmap'
    const showBiologyOverlayStack = showBiologyLegend || showHeatmapInfoOverlay
    const showMapResizeGrip = filteredData.length > 0 || Boolean(selectedPointData)
    const renderBasemapOptions = (mobile = false) => (
        BASEMAP_OPTIONS.map(option => {
            const selected = option.id === activeBasemapId
            return (
                <Button
                    key={option.id}
                    fullWidth
                    onClick={() => {
                        setActiveBasemapId(option.id)
                        setBasemapMenuOpen(false)
                    }}
                    sx={{
                        minHeight: mobile ? 50 : 38,
                        justifyContent: 'space-between',
                        px: mobile ? 2 : 1.5,
                        borderRadius: 0,
                        color: selected ? '#FFF5E6' : '#f2f4f6',
                        backgroundColor: selected ? 'rgba(32, 178, 170, 0.22)' : 'transparent',
                        textTransform: 'none',
                        fontSize: mobile ? '15px' : '13px',
                        fontWeight: selected ? 700 : 500,
                        '&:hover': {
                            backgroundColor: selected ? 'rgba(32, 178, 170, 0.28)' : 'rgba(32, 178, 170, 0.12)'
                        }
                    }}
                >
                    <span>{option.label}</span>
                    {selected && <CheckIcon sx={{ fontSize: mobile ? 19 : 17, color: '#20B2AA' }} />}
                </Button>
            )
        })
    )

    const elevateMobileFilterStack = shouldCollapseFilterAfterApply && !isFilterCollapsed

    // render the map holding container
    return (
        <>
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                backgroundColor: '#2D3436', // Warm charcoal background
                margin: 0,
                padding: 0,
                height: 'auto', // Changed from '100vh' to 'auto' to allow content to expand
                minHeight: '100vh' // Added minHeight to ensure it takes at least full viewport height
            }}>
                {/* Streamlined header bar with tabs */}
                <Box sx={{
                    width: '100%',
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    backgroundColor: '#2D3436', // Warm charcoal
                    height: '100px',
                    paddingRight: 0,
                    paddingLeft: { xs: '8px', sm: '24px' },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    zIndex: 1002, // Higher z-index to stay above filter overlays
                    borderBottom: '1px solid rgba(32, 178, 170, 0.1)', // Subtle Light Sea Green border
                    overflow: 'hidden'
                }}>
                    {/* Logo and title container */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        height: '100%',
                        flexShrink: 0,
                        minWidth: 0
                    }}>
                        {/* Logo with transparent background */}
                        <Box
                            component="img"
                            src={seamapLogo}
                            alt="SEAMAP Logo"
                            sx={{
                                height: { xs: 58, sm: 70 },
                                mr: { xs: 1, sm: 2 },
                                filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.2))'
                            }}
                        />
                        <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center' }}>
                            <Typography
                                component="span"
                                sx={{
                                    color: '#FFF5E6', // Warm cream
                                    fontWeight: 700,
                                    fontSize: '1.5rem',
                                    letterSpacing: '0.5px'
                                }}
                            >
                                SEAMAP
                            </Typography>
                            <Typography
                                component="span"
                                sx={{
                                    color: '#66D4D0', // Light Sea Green separator
                                    fontWeight: 300,
                                    fontSize: '1.5rem',
                                    mx: 1
                                }}
                            >
                                |
                            </Typography>
                            <Typography
                                component="span"
                                sx={{
                                    color: '#FFF5E6', // Warm cream
                                    fontWeight: 500,
                                    fontSize: '1.5rem',
                                    letterSpacing: '0.5px',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                WebMap
                            </Typography>
                        </Box>
                    </Box>

                    {/* Navigation tabs */}
                    <MaterialTabs activeTab={activeTab} onTabChange={handleTabClick} />
                </Box>

                {/* Map container with responsive height */}
                <Box ref={mapAreaRef} sx={{
                    position: 'relative',
                    width: '100%',
                    marginTop: '100px', // Add margin to account for fixed header
                    height: customMapHeight
                        ? `${customMapHeight}px`
                        : {
                            xs: 'calc(50vh - 50px)', // Mobile: 50% of viewport height minus header
                            sm: 'calc(55vh - 50px)', // Tablet: 55% of viewport height minus header
                            md: 'calc(60vh - 50px)', // Desktop: 60% of viewport height minus header
                            lg: 'calc(75vh - 100px)', // Large desktop (1080p): 75% of viewport height minus header (reduced by ~10%)
                            xl: 'calc(77vh - 100px)'  // Extra large (ultrawide): 77% for better table visibility (reduced by ~8%)
                        },
                    minHeight: customMapHeight
                        ? `${MIN_MAP_HEIGHT_PX}px`
                        : {
                            xs: '300px', // Minimum height on mobile
                            sm: '400px', // Minimum height on tablet
                            md: '500px'  // Minimum height on desktop and above
                        },
                    maxHeight: customMapHeight
                        ? 'none'
                        : {
                            xs: '400px', // Maximum height on mobile
                            sm: '500px', // Maximum height on tablet
                            md: '600px', // Maximum height on small desktop
                            lg: 'none'   // Remove max height restriction for large screens
                        },
                    backgroundColor: 'white',
                    overflow: 'hidden', // Prevent any potential scrolling
                    zIndex: elevateMobileFilterStack ? 1001 : 0 // Keep expanded mobile filters above lower page content but below the header.
                }}>
                    {/* Loading overlay */}
                    {isLoading && (
                        <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'rgba(26, 42, 58, 0.7)',
                            zIndex: 1500
                        }}>
                            <CircularProgress sx={{ color: '#b8d8d0' }} />
                        </Box>
                    )}

                    {(areaSelectionEnabled || biologyHeatmapLoading || biologyHeatmapError) && (
                        <Box sx={{
                            position: 'absolute',
                            left: 16,
                            bottom: 16,
                            zIndex: 1200,
                            backgroundColor: 'rgba(45, 52, 54, 0.92)',
                            color: '#f2f4f6',
                            borderRadius: '4px',
                            px: 1.5,
                            py: 1,
                            fontSize: '12px',
                            fontWeight: 700,
                            boxShadow: '0 2px 10px rgba(0,0,0,0.25)'
                        }}>
                            {areaSelectionEnabled
                                ? 'Drag on the map to select an export area'
                                : biologyHeatmapLoading
                                    ? 'Loading heat map...'
                                    : biologyHeatmapError}
                        </Box>
                    )}

                    <Box
                        ref={basemapMenuRef}
                        sx={{
                            position: 'absolute',
                            top: 16,
                            right: { xs: 8, md: 16 },
                            zIndex: 1300,
                            display: 'grid',
                            justifyItems: 'end',
                            gap: 1
                        }}
                    >
                        <IconButton
                            aria-label="Basemap layers"
                            onClick={() => setBasemapMenuOpen(open => !open)}
                            sx={{
                                width: 40,
                                height: 40,
                                color: '#20B2AA',
                                backgroundColor: 'rgba(45, 52, 54, 0.95)',
                                border: '1px solid rgba(32, 178, 170, 0.18)',
                                borderRadius: '4px',
                                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                                backdropFilter: 'blur(8px)',
                                '&:hover': {
                                    backgroundColor: 'rgba(45, 52, 54, 1)'
                                }
                            }}
                        >
                            <LayersIcon sx={{ fontSize: 21 }} />
                        </IconButton>
                        {basemapMenuOpen && !isMobileBasemapMenu && (
                            <Paper
                                elevation={0}
                                sx={{
                                    width: 190,
                                    backgroundColor: 'rgba(45, 52, 54, 0.95)',
                                    border: '1px solid rgba(32, 178, 170, 0.18)',
                                    borderRadius: '4px',
                                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                                    backdropFilter: 'blur(8px)',
                                    overflow: 'hidden'
                                }}
                            >
                                {renderBasemapOptions(false)}
                            </Paper>
                        )}
                    </Box>

                    <MapContainer
                        id="map-container"
                        center={[28.6, -89]}
                        zoom={7}
                        zoomControl={false}
                        doubleClickZoom={false}
                        style={{
                            width: '100%',
                            height: '100%',
                            backgroundColor: activeBasemap.type === 'vector' ? MINIMAL_BASEMAP.land : '#20383d'
                        }}
                    >
                        {activeBasemap.type === 'tile' && (
                            <TileLayer
                                key={activeBasemap.id}
                                url={activeBasemap.url}
                                className="seamap-raster-basemap-tile"
                                maxZoom={activeBasemap.maxZoom}
                                maxNativeZoom={activeBasemap.maxNativeZoom}
                                crossOrigin="anonymous"
                                updateWhenIdle
                                updateWhenZooming={false}
                                keepBuffer={12}
                            />
                        )}
                        {activeBasemap.type === 'vector' && (
                            <MinimalBasemapLayer />
                        )}

                        {/* Store map reference */}
                        <MapReference />
                        <AreaSelectionControl
                            enabled={areaSelectionEnabled}
                            onPreviewChange={setAreaSelectionPreview}
                            onComplete={handleAreaSelectionComplete}
                        />

                        {/* Sampling zone shapefiles stay mounted so repeated toggles only add/remove the cached Leaflet layer. */}
                        <ShapefileLayer
                            key="trawl-current"
                            url={`${process.env.PUBLIC_URL}/spatial/Trawl_WGS84.shp`}
                            borderColor="#FF3366"
                            fillColor="#FF6699"
                            opacity={0.8}
                            fillOpacity={0.3}
                            visible={isTrawlTab && showTrawlShapefile}
                            preload
                        />

                        <ShapefileLayer
                            key="trawl-historic"
                            url={`${process.env.PUBLIC_URL}/spatial/HistoricTrawlSamplingArea.shp`}
                            borderColor="#00CCFF"
                            fillColor="#66E0FF"
                            opacity={0.8}
                            fillOpacity={0.3}
                            visible={isTrawlTab && showHistoricTrawlShapefile}
                            preload
                        />

                        <ShapefileLayer
                            key="longline-2008"
                            url={`${process.env.PUBLIC_URL}/spatial/BLL_WGS84_2008.shp`}
                            borderColor="#FF5733"
                            fillColor="#FF8C66"
                            opacity={0.8}
                            fillOpacity={0.3}
                            visible={activeTab.includes('Longline') && showLongline2008Shapefile}
                            preload
                        />

                        <ShapefileLayer
                            key="longline-2015"
                            url={`${process.env.PUBLIC_URL}/spatial/BLL_WGS84_2015.shp`}
                            borderColor="#33CAFF"
                            fillColor="#7AD7FF"
                            opacity={0.8}
                            fillOpacity={0.3}
                            visible={activeTab.includes('Longline') && showLongline2015Shapefile}
                            preload
                        />

                        <ShapefileLayer
                            key="longline-2024"
                            url={`${process.env.PUBLIC_URL}/spatial/BLL_WGS84_2024_buffer.shp`}
                            borderColor="#66FF66"
                            fillColor="#99FF99"
                            opacity={0.8}
                            fillOpacity={0.3}
                            visible={activeTab.includes('Longline') && showLongline2024Shapefile}
                            preload
                        />

                        {mapDisplayMode === 'heatmap' && biologyHeatmapGrid?.cells?.length > 0 && (
                            <BiologyHeatmapGridLayer
                                cells={biologyHeatmapGrid.cells}
                                metadata={biologyHeatmapGrid.metadata}
                                scale={biologyHeatmapScale}
                                areaSelectionEnabled={areaSelectionEnabled}
                                basemapId={activeBasemapId}
                            />
                        )}

                        {areaSelectionPreview && (
                            <Rectangle
                                bounds={areaSelectionPreview}
                                pathOptions={{
                                    color: '#2E5A88',
                                    weight: 2,
                                    dashArray: '6 4',
                                    fillColor: '#66D4D0',
                                    fillOpacity: 0.14
                                }}
                                interactive={false}
                            />
                        )}

                        {/* Add MapBoundsControl only when we explicitly want to fit bounds */}
                        {(stationMarkerModels.length > 0 || biologyMarkerModels.length > 0) && shouldFitBoundsRef.current && (
                            <MapBoundsControl markers={isStationTab ? stationMarkerModels : biologyMarkerModels} />
                        )}

                        {/* For station tabs */}
                        {isStationTab && stationMarkerModels.length > 0 && (
                                    stationMarkerModels.map((marker) => {
                                        return (
                                            <CircleMarker
                                                key={marker.key}
                                                center={[marker.lat, marker.lon]}
                                                eventHandlers={{ click: () => handleMarkerClick(marker.item) }}
                                                radius={4}
                                                pathOptions={{
                                                    color: '#333333',
                                                    weight: 0.8,
                                                    // Station points are NOT species-coded so they don't go
                                                    // through getSpeciesColor â€” pick a brand-friendly hue here
                                                    // that pops on both ESRI satellite land tiles AND shallow
                                                    // Gulf water. Deep pink (#FF1493) was chosen because it
                                                    // sits well outside the land-tone wedge enforced by
                                                    // isLandTone() and is highly visible against sand, marsh,
                                                    // and water alike. Do not switch this back to a tan/sand
                                                    // tone â€” coastal stations vanish into the basemap.
                                                    fillColor: '#FF1493',
                                                    fillOpacity: 1,
                                                    opacity: 1
                                                }}
                                            >
                                                <Popup className="point-popup">
                                                    <div>
                                                        <p>{`Station ID: ${marker.displayStationId}`}</p>
                                                        {marker.item.valid_swept_area !== undefined && (
                                                            <p>
                                                                Swept area: {marker.item.valid_swept_area ? `${formatMapNumber(getSweptAreaHectares(marker.item), 2)} hectares` : 'invalid'}
                                                            </p>
                                                        )}
                                                    </div>
                                                </Popup>
                                            </CircleMarker>
                                        )
                                    })
                        )}

                        {/* For biology tabs */}
                        {isBiologyTab && biologyMarkerModels.length > 0 && mapDisplayMode === 'markers' && (
                                    biologyMarkerModels.map((marker) => {
                                        return (
                                            <CircleMarker
                                                key={marker.key}
                                                center={[marker.lat, marker.lon]}
                                                eventHandlers={{ click: () => handleMarkerClick(marker.item) }}
                                                radius={4}
                                                pathOptions={{
                                                    color: '#333333',
                                                    weight: 0.8,
                                                    fillColor: marker.fillColor,
                                                    fillOpacity: 1,
                                                    opacity: 1
                                                }}
                                            >
                                                <Popup className="point-popup">
                                                    <div>
                                                        <p>{`Station ID: ${marker.displayStationId}`}</p>
                                                        {marker.item.valid_swept_area !== undefined && (
                                                            <p>
                                                                Swept area: {marker.item.valid_swept_area ? `${formatMapNumber(getSweptAreaHectares(marker.item), 2)} hectares` : 'invalid'}
                                                            </p>
                                                        )}
                                                    </div>
                                                </Popup>
                                            </CircleMarker>
                                        )
                                    })
                        )}

                        {/* Show appropriate filter overlay based on tab type. Wrapped in a
                            display-toggling Box so the position:fixed overlay disappears once
                            the user scrolls past the map area entirely. */}
                        {isStationTab && (
                            <Box sx={{ display: isMapVisible ? 'block' : 'none' }}>
                                <FilterOverlay
                                    selectedState={selectedState}
                                    setSelectedState={setSelectedState}
                                    selectedYear={selectedYear}
                                    setSelectedYear={setSelectedYear}
                                    selectedSeason={selectedSeason}
                                    setSelectedSeason={setSelectedSeason}
                                    handleFilter={handleStationsFilter}
                                    states={getStatesForTab()}
                                    years={getYearsForTab()}
                                    activeTab={activeTab}
                                    showTrawlShapefile={showTrawlShapefile}
                                    setShowTrawlShapefile={setShowTrawlShapefile}
                                    showHistoricTrawlShapefile={showHistoricTrawlShapefile}
                                    setShowHistoricTrawlShapefile={setShowHistoricTrawlShapefile}
                                    showLongline2008Shapefile={showLongline2008Shapefile}
                                    setShowLongline2008Shapefile={setShowLongline2008Shapefile}
                                    showLongline2015Shapefile={showLongline2015Shapefile}
                                    setShowLongline2015Shapefile={setShowLongline2015Shapefile}
                                    showLongline2024Shapefile={showLongline2024Shapefile}
                                    setShowLongline2024Shapefile={setShowLongline2024Shapefile}
                                    isCollapsed={isFilterCollapsed}
                                    setIsCollapsed={setIsFilterCollapsed}
                                    dateRangeStart={dateRangeStart}
                                    setDateRangeStart={setDateRangeStart}
                                    dateRangeEnd={dateRangeEnd}
                                    setDateRangeEnd={setDateRangeEnd}
                                />
                            </Box>
                        )}

                        {isBiologyTab && (
                            <Box sx={{ display: isMapVisible ? 'block' : 'none' }}>
                                {renderBiologyFilterOverlay()}
                            </Box>
                        )}

                        {/* Floating biology overlays, stacked at the right edge. */}
                        {showBiologyOverlayStack && (
                            <Box sx={{
                                position: 'absolute',
                                top: basemapMenuOpen ? 190 : 72,
                                right: { xs: 8, md: 16 },
                                bottom: 16,
                                zIndex: 1000,
                                width: { xs: 'min(250px, calc(100vw - 32px))', md: 250 },
                                display: 'grid',
                                gridTemplateRows: showBiologyLegend && showHeatmapInfoOverlay
                                    ? (isDetailsPanelCollapsed ? 'auto auto' : 'auto minmax(0, 1fr)')
                                    : 'auto',
                                alignContent: 'start',
                                gap: 1,
                                pointerEvents: 'none',
                                [COMPACT_BIOLOGY_OVERLAY_QUERY]: {
                                    top: basemapMenuOpen ? 186 : 68,
                                    right: 8,
                                    width: 'min(228px, calc(100vw - 16px))',
                                    gap: 0.75
                                },
                                '& > *': {
                                    pointerEvents: 'auto'
                                }
                            }}>
                                {showBiologyLegend && (
                                    <Box sx={{
                                        backgroundColor: 'rgba(45, 52, 54, 0.95)',
                                        backdropFilter: 'blur(8px)',
                                        border: '1px solid rgba(32, 178, 170, 0.08)',
                                        borderRadius: '4px',
                                        p: 1.5,
                                        width: '100%',
                                        height: 'fit-content',
                                        minHeight: 'max-content',
                                        maxHeight: 'none',
                                        overflow: 'visible',
                                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                                        [COMPACT_BIOLOGY_OVERLAY_QUERY]: {
                                            p: 1,
                                        },
                                    }}>
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            borderBottom: isLegendPanelCollapsed ? 'none' : '1px solid rgba(255, 245, 230, 0.2)',
                                            pb: isLegendPanelCollapsed ? 0 : 1,
                                            mb: isLegendPanelCollapsed ? 0 : 1.5,
                                            [COMPACT_BIOLOGY_OVERLAY_QUERY]: {
                                                pb: isLegendPanelCollapsed ? 0 : 0.75,
                                                mb: isLegendPanelCollapsed ? 0 : 1
                                            }
                                        }}>
                                            <Typography variant="h6" sx={{
                                                fontSize: '14px',
                                                color: '#FFF5E6',
                                                fontWeight: 'bold',
                                                lineHeight: 1.5,
                                                flex: 1,
                                                minWidth: 0,
                                                [COMPACT_BIOLOGY_OVERLAY_QUERY]: {
                                                    fontSize: '13px'
                                                }
                                            }}>
                                                Legend
                                            </Typography>
                                            <IconButton
                                                size="small"
                                                onClick={() => setIsLegendPanelCollapsed(prev => !prev)}
                                                aria-label={isLegendPanelCollapsed ? 'Expand legend panel' : 'Collapse legend panel'}
                                                sx={{
                                                    color: '#FFF5E6',
                                                    p: 0.25,
                                                    ml: 1,
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                                    }
                                                }}
                                            >
                                                {isLegendPanelCollapsed ? <KeyboardArrowDown sx={{ fontSize: '20px' }} /> : <KeyboardArrowUp sx={{ fontSize: '20px' }} />}
                                            </IconButton>
                                        </Box>
                                        {!isLegendPanelCollapsed && (
                                            <>
                                        <Box sx={{
                                            maxHeight: 'none',
                                            overflow: 'visible',
                                            pr: 0,
                                            [COMPACT_BIOLOGY_OVERLAY_QUERY]: {
                                                pr: 0
                                            },
                                        }}>
                                            {biologyLegendItems.map((species, index) => (
                                                <Box key={index} sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    mb: 1,
                                                    fontSize: '12px',
                                                    transition: 'background-color 0.2s',
                                                    p: 0.5,
                                                    borderRadius: 1,
                                                    [COMPACT_BIOLOGY_OVERLAY_QUERY]: {
                                                        mb: 0.6,
                                                        p: 0.25
                                                    },
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                                    }
                                                }}>
                                                    <Box sx={{
                                                        width: 14,
                                                        height: 14,
                                                        backgroundColor: species.color,
                                                        border: '1.5px solid rgba(255, 255, 255, 0.8)',
                                                        borderRadius: '50%',
                                                        mr: 1.5,
                                                        flexShrink: 0,
                                                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
                                                        [COMPACT_BIOLOGY_OVERLAY_QUERY]: {
                                                            width: 12,
                                                            height: 12,
                                                            mr: 1
                                                        }
                                                    }} />
                                                    <Typography sx={{
                                                        fontSize: '12px',
                                                        color: '#f2f4f6',
                                                        lineHeight: 1.3,
                                                        wordBreak: 'break-word',
                                                        fontWeight: 500,
                                                        [COMPACT_BIOLOGY_OVERLAY_QUERY]: {
                                                            fontSize: '11.5px',
                                                            lineHeight: 1.2
                                                        }
                                                    }}>
                                                        {species.name}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                        {mapDisplayMode === 'heatmap' && biologyHeatmapGrid?.metadata && (
                                            <BiologyHeatmapLegend
                                                metadata={biologyHeatmapGrid.metadata}
                                                scale={biologyHeatmapScale}
                                                zeroCellBorderColor={heatmapZeroCellBorderColor}
                                            />
                                        )}
                                            </>
                                        )}
                                    </Box>
                                )}
                                {showHeatmapInfoOverlay && (
                                    <BiologyHeatmapInfoOverlay
                                        metadata={biologyHeatmapGrid?.metadata}
                                        loading={biologyHeatmapLoading}
                                        error={biologyHeatmapError}
                                        selectionEnabled={areaSelectionEnabled}
                                        collapsed={isDetailsPanelCollapsed}
                                        onToggle={() => setIsDetailsPanelCollapsed(prev => !prev)}
                                        onOverlayPointerEnter={pauseMapScrollWheelForOverlay}
                                        onOverlayPointerLeave={restoreMapScrollWheelAfterOverlay}
                                    />
                                )}
                            </Box>
                        )}
                    </MapContainer>
                </Box>

                {/* Results export strip â€” visible the moment a query returns
                    so users can pull a CSV of all displayed points without
                    having to click an individual point first. The detail
                    table below still appears only on point selection. */}
                {showMapResizeGrip && (
                    <Box
                        ref={resizeGripRef}
                        role="separator"
                        aria-label="Resize map and results panel"
                        aria-orientation="horizontal"
                        aria-valuenow={customMapHeight || undefined}
                        tabIndex={0}
                        onPointerDown={handleMapResizePointerDown}
                        onKeyDown={handleMapResizeKeyDown}
                        onDoubleClick={resetMapHeight}
                        sx={{
                            height: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'row-resize',
                            backgroundColor: '#2D3436',
                            borderTop: '1px solid rgba(32, 178, 170, 0.16)',
                            borderBottom: '1px solid rgba(32, 178, 170, 0.22)',
                            touchAction: 'none',
                            userSelect: 'none',
                            '&::before': {
                                content: '""',
                                width: '64px',
                                height: '4px',
                                borderRadius: '4px',
                                backgroundColor: 'rgba(32, 178, 170, 0.75)',
                                boxShadow: '0 1px 4px rgba(0, 0, 0, 0.28)'
                            },
                            '&:hover::before, &:focus-visible::before': {
                                backgroundColor: '#20B2AA'
                            },
                            '&:focus-visible': {
                                outline: '2px solid #20B2AA',
                                outlineOffset: '-2px'
                            }
                        }}
                    />
                )}

                <Box ref={resultsBarRef}>
                    <ResultsExportBar
                        activeTab={activeTab}
                        rows={filteredData}
                        queryContext={currentQueryContext}
                        displayMode={mapDisplayMode}
                        onDisplayModeChange={handleDisplayModeChange}
                        heatmapMetric={heatmapMetric}
                        onHeatmapMetricChange={setHeatmapMetric}
                        selectionEnabled={areaSelectionEnabled}
                        onStartAreaSelect={() => setAreaSelectionEnabled(prev => !prev)}
                    />
                </Box>

                <Box
                    ref={returnNavRef}
                    component="nav"
                    aria-label="WebMap return navigation"
                    sx={{
                        position: 'sticky',
                        bottom: { xs: 12, sm: 16 },
                        left: { xs: 12, sm: 16 },
                        zIndex: 1201,
                        width: 'fit-content',
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        ml: { xs: 1.5, sm: 2 },
                        my: { xs: 1.5, sm: 2 }
                    }}
                >
                    <Button
                        component="a"
                        href={returnLinkConfig.href}
                        aria-label={returnLinkConfig.label}
                        sx={{
                            minHeight: 44,
                            px: 2.25,
                            borderRadius: '4px',
                            backgroundColor: '#20B2AA',
                            color: '#FFF5E6',
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '14px',
                            lineHeight: 1.2,
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.18)',
                            '&:hover': {
                                backgroundColor: '#1A9B99',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.22)',
                                transform: 'translateY(-1px)'
                            },
                            '&::before': {
                                content: '"\\2190"',
                                fontWeight: 800,
                                fontSize: '15px',
                                lineHeight: 1,
                                mr: 0.75
                            }
                        }}
                    >
                        {returnLinkConfig.label}
                    </Button>
                </Box>

                {/* Data tables - responsive container */}
                <Box
                    ref={tablePanelRef}
                    onPointerDown={handleTablePanelPointerDown}
                    onPointerMove={handleTablePanelPointerMove}
                    onPointerUp={handleTablePanelPointerEnd}
                    onPointerCancel={handleTablePanelPointerEnd}
                    onLostPointerCapture={handleTablePanelPointerEnd}
                    onClickCapture={handleTablePanelClickCapture}
                    sx={{
                        width: '100%',
                        padding: selectedPointData ? { xs: '0.5rem', sm: '1rem' } : 0,
                        backgroundColor: 'white',
                        overflow: 'auto', // Enable scrolling for tables
                        display: selectedPointData ? 'block' : 'none',
                        height: resizedTablePanelHeight ? `${resizedTablePanelHeight}px` : 'auto',
                        maxHeight: resizedTablePanelHeight ? `${resizedTablePanelHeight}px` : {
                            xs: 'calc(50vh - 50px)', // Mobile: remaining viewport height
                            sm: 'calc(45vh - 50px)', // Tablet: remaining viewport height
                            md: 'calc(40vh - 50px)', // Desktop: remaining viewport height
                            lg: 'calc(35vh - 50px)', // Large desktop: remaining viewport height
                            xl: '400px'              // Extra large: fixed height
                        },
                        minHeight: resizedTablePanelHeight ? `${resizedTablePanelHeight}px` : {
                            xs: '200px', // Minimum height for tables on mobile
                            sm: '250px', // Minimum height for tables on tablet
                            md: '300px'  // Minimum height for tables on desktop
                        },
                        cursor: selectedPointData ? (isTableDragScrolling ? 'grabbing' : 'grab') : 'default',
                        userSelect: isTableDragScrolling ? 'none' : 'auto',
                        scrollbarGutter: 'stable'
                    }}
                >
                    {activeTab === 'TrawlStations' && selectedPointData && (
                        <PointDataTableTrawl data={selectedPointData} allRows={filteredData} />
                    )}
                    {activeTab === 'LonglineStations' && selectedPointData && (
                        <PointDataTableLongline data={selectedPointData} allRows={filteredData} />
                    )}
                    {activeTab === 'PlanktonStations' && selectedPointData && (
                        <PointDataTablePlankton data={selectedPointData} allRows={filteredData} />
                    )}
                    {activeTab === 'LonglineBiology' && selectedPointData && (
                        <PointDataTableLonglineBiology data={selectedPointData} allRows={filteredData} />
                    )}
                    {activeTab === 'TrawlBiology' && selectedPointData && (
                        <PointDataTableTrawlBiology data={selectedPointData} allRows={filteredData} />
                    )}
                    {activeTab === 'PlanktonBiology' && selectedPointData && (
                        <PointDataTablePlanktonBiology data={selectedPointData} allRows={filteredData} />
                    )}
                </Box>

                {/* Footer bar */}
                <Box ref={footerRef} sx={{
                    width: '100%',
                    height: '40px', // Slightly reduced
                    display: selectedPointData ? 'none' : 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '14px'
                }}>
                    {/* Footer content - can be added later */}
                </Box>
            </Box>

            <Drawer
                anchor="bottom"
                open={basemapMenuOpen && isMobileBasemapMenu}
                onClose={() => setBasemapMenuOpen(false)}
                PaperProps={{
                    sx: {
                        backgroundColor: '#2D3436',
                        color: '#FFF5E6',
                        borderTop: '1px solid rgba(32, 178, 170, 0.24)',
                        borderTopLeftRadius: '8px',
                        borderTopRightRadius: '8px',
                        overflow: 'hidden'
                    }
                }}
                ModalProps={{ keepMounted: true }}
            >
                <Box sx={{ px: 2, pt: 2, pb: 'calc(12px + env(safe-area-inset-bottom))' }}>
                    <Typography
                        id="basemap-bottom-sheet-title"
                        sx={{
                            mb: 1,
                            color: '#20B2AA',
                            fontSize: '13px',
                            fontWeight: 800,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                        }}
                    >
                        Basemap
                    </Typography>
                    <Box
                        role="menu"
                        aria-labelledby="basemap-bottom-sheet-title"
                        sx={{
                            overflow: 'hidden',
                            border: '1px solid rgba(32, 178, 170, 0.18)',
                            borderRadius: '4px'
                        }}
                    >
                        {renderBasemapOptions(true)}
                    </Box>
                </Box>
            </Drawer>

            <Dialog
                open={areaSelectionDialogOpen}
                onClose={handleClearAreaSelection}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ color: '#FFF5E6', fontWeight: 700 }}>
                    Export Selected Area
                </DialogTitle>
                <DialogContent>
                    {areaSelectionLoading && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
                            <CircularProgress size={20} sx={{ color: '#FFF5E6' }} />
                            <Typography sx={{ color: '#FFF5E6' }}>Loading selected records...</Typography>
                        </Box>
                    )}
                    {!areaSelectionLoading && areaSelectionError && (
                        <Typography sx={{ color: '#b91c1c', fontWeight: 700 }}>
                            {areaSelectionError}
                        </Typography>
                    )}
                    {!areaSelectionLoading && areaSelectionData && (
                        <Box sx={{ display: 'grid', gap: 1, py: 1 }}>
                            <Typography sx={{ color: '#FFF5E6' }}>
                                {(areaSelectionData.counts?.stations || 0).toLocaleString()} station records selected
                            </Typography>
                            <Typography sx={{ color: '#FFF5E6' }}>
                                {(areaSelectionData.counts?.catches || 0).toLocaleString()} catch records selected
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClearAreaSelection} sx={{ textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleAreaSelectionExport}
                        disabled={!areaSelectionData || areaSelectionLoading}
                        sx={{ textTransform: 'none', backgroundColor: '#2E5A88' }}
                    >
                        Export ZIP
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={
                    activeTab === 'LonglineStations' ? 3000 :
                    (snackbarMessage.includes('Loading Trawl Biology data') ? 3000 : 10000)
                }
                onClose={handleSnackbarClose}
                message={snackbarMessage}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                ContentProps={{
                    sx: {
                        backgroundColor: '#4a7c99', // var(--color-coastal-blue)
                        color: '#f2f4f6', // var(--color-offwhite)
                        fontWeight: 500,
                        fontFamily: "'Nunito', sans-serif",
                        minWidth: '200px',
                        justifyContent: 'center',
                        padding: '10px 15px',
                        borderRadius: '4px',
                        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
                    }
                }}
                action={
                    <IconButton
                        size="small"
                        aria-label="close"
                        color="inherit"
                        onClick={handleSnackbarClose}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                }
            />
        </>
    )
}

export default MapView
